/* eslint-disable no-unused-vars, react/prop-types, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, IconButton, Chip, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Menu, MenuItem, ListItemIcon,
  ListItemText, Divider, TextField, Select, Autocomplete, Tooltip,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { languageName, ownTranslatedText, LANGUAGE_SOURCE_TEXT, templatePlaceholderName } from './messageLanguage';
import { deliveryFailureText } from './deliveryFailure';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  Chat as ChatIcon,
  Sms as SmsIcon,
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Image as ImageIcon,
  Movie as MovieIcon,
  PlayCircle as PlayCircleIcon,
  Description as DescriptionIcon,
  ArrowBack as ArrowBackIcon,
  MoreVert as MoreVertIcon,
  DoneAll as DoneAllIcon,
  Flag as FlagIcon,
  AccessTime as AccessTimeIcon,
  PhoneCallback as PhoneCallbackIcon,
  PhoneForwarded as PhoneForwardedIcon,
  Close as CloseIcon,
  MarkChatRead as MarkChatReadIcon,
  VisibilityOff as VisibilityOffIcon,
  Cancel as CancelIcon,
  Article as TemplateIcon,
  Language as LanguageIcon,
  Translate as TranslateIcon,
  Mic as MicIcon,
  StopCircle as StopCircleIcon,
  GraphicEq as VoiceNoteIcon,
  AutoFixHigh as AutoFixHighIcon,
  Spellcheck as SpellcheckIcon,
  AddReaction as AddReactionIcon,
  Schedule as ScheduleIcon,
  Event as EventIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import {
  getConversation, getConversationMailPolicy, getConversationMessages, getConversationMessageAnchor, sendMessage, markConversationAsRead,
  getTwilioWhatsAppTemplates,
  sendSms, sendWhatsApp, sendFacebook, sendInstagram, sendEmailFromTunnel, initiateCall, closeConversation,
  updateConversationTriage, getConversationRepairLinks, uploadConversationAttachment, resolveCommunicationAttachmentUrl,
  transcribeAndEnhanceAudio, transcribeMessageAudioAttachment, enhanceTranscribedAudioText, suggestConversationReply, proofreadText,
  setMessageReaction, getRecordingProxyUrl, transcribeCallRecording, broadcastToGroup, getGroupParticipants, ChannelType, MessageDirection,
  scheduleMessage, getScheduledMessages, cancelScheduledMessage, updateScheduledMessage,
  generateAiResponseDraft, submitAiResponseFeedback, getLearningRecommendations, approveLearningRecommendation,
  getConversationChatbotStatus, toggleConversationChatbot
} from '../../api/communicationApi';
import { useAuth } from '../userManagement/AuthContext';
import CallModal from './CallModal';
import CallTranscript from './CallTranscript';
import ChatImageAttachment from './ChatImageAttachment';
import ChatMediaAttachment from './ChatMediaAttachment';
import ChatFileAttachment from './ChatFileAttachment';
import ActorQuickInfoDialog from './ActorQuickInfoDialog';
import CustomerRecognitionBar from './CustomerRecognitionBar';
import {
  TEMPLATE_LANGUAGES,
  MESSAGE_TEMPLATES,
  findTemplateCommandMatches,
  resolveTemplateCommand,
} from './messageTemplates';
import { ACCOUNT_LANGUAGES, getAccountLanguage } from '../../utils/accountLanguage';
import useLiveTranslation from './useLiveTranslation';
import './CommunicationTunnel.css';

import WorkTaskPanel from '../WorkTasks/WorkTaskPanel';
import T, { useAutoTranslate } from '../common/T';
import PriorContactPanel from './PriorContactPanel';
const GREEN = '#32cd32';
const TEMPLATE_LANGUAGE_STORAGE_KEY = 'communication_template_language';
const REACTION_OPTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

const AI_LANGUAGE_OPTIONS = ACCOUNT_LANGUAGES;

// Helper: Convert any Date / string into Local ISO string (YYYY-MM-DDTHH:mm) for input type="datetime-local"
const getLocalISOString = (dateObj = new Date()) => {
  const d = new Date(dateObj);
  if (isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Helper: Format date as European DD/MM/YYYY HH:mm for display
const formatDDMMYYYY = (dateObj) => {
  if (!dateObj) return '';
  const d = new Date(dateObj);
  if (isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

// Helper to strip HTML tags and get clean text preview
const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

const cleanDisplayText = (value) => {
  const text = String(value || '');
  if (!/<\/?[a-z][\s\S]*>/i.test(text) && !/&lt;\/?[a-z]/i.test(text)) {
    return text;
  }
  return stripHtml(text).trim();
};

const getMessagesSignature = (items = []) => (
  items.map((msg) => [
    msg.id,
    msg.delivery_status || '',
    msg.message_metadata?.twilio_error_code || '',
    msg.message_metadata?.twilio_error_message || '',
    msg.is_read ? '1' : '0',
    msg.content || '',
    msg.content_html || '',
    msg.created_at || '',
    JSON.stringify(msg.message_metadata?.reactions || []),
    Array.isArray(msg.attachments) ? msg.attachments.length : 0,
  ].join(':')).join('|')
);

const LANGUAGE_NAMES = { de: 'German', en: 'English', fr: 'French', it: 'Italian', es: 'Spanish', pt: 'Portuguese', nl: 'Dutch', sq: 'Albanian', tr: 'Turkish', ar: 'Arabic', sr: 'Serbian', hr: 'Croatian', pl: 'Polish' };

// A translation outage comes back as a structured detail so the composer can
// offer an explicit choice instead of a dead end.
const translationOutageOf = (err) => {
  const detail = err?.response?.data?.detail;
  return detail && typeof detail === 'object' && detail.code === 'translation_unavailable' ? detail : null;
};

const formatApiError = (err, fallback = 'Something went wrong') => {
  const data = err?.response?.data;
  let detail = data?.detail || data?.message || data?.error || data || err?.message || fallback;

  if (typeof detail === 'string') {
    const trimmed = detail.trim();
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try {
        const parsed = JSON.parse(trimmed);
        return parsed.detail || parsed.message || parsed.error || trimmed;
      } catch {
        return detail;
      }
    }
    return detail;
  }

  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg || item?.message || JSON.stringify(item)).join(', ');
  }

  if (detail && typeof detail === 'object') {
    return detail.detail || detail.message || detail.error || JSON.stringify(detail);
  }

  return fallback;
};

// Friendly labels for notification event types
const EVENT_TYPE_LABELS = {
  'business_process_created': 'Repair process created',
  'business_process_updated': 'Repair process updated',
  'business_process_started': 'Repair process started',
  'diagnostic_offer': 'Diagnostic offer sent',
  'diagnostic_phase': 'Diagnostic phase notification',
  'repair_feedback_request': 'Feedback request sent',
  'repair_invoice': 'Invoice sent',
  'repair_quote': 'Repair quote sent',
  'repair_completed': 'Repair completed notification',
  'repair_status_update': 'Status update sent',
  'logistics_confirmed': 'Logistics confirmation sent',
  'drop_off_station_request': 'Drop-off station request',
  'vip_express': 'VIP Express notification',
};

const CHANNEL_COLORS = {
  [ChannelType.EMAIL]: { main: '#1976d2', bg: '#e3f2fd', border: '#1976d2' },
  [ChannelType.PHONE]: { main: '#2e7d32', bg: '#e8f5e9', border: '#2e7d32' },
  [ChannelType.WHATSAPP]: { main: '#25d366', bg: '#e8f5e9', border: '#25d366' },
  [ChannelType.SMS]: { main: '#0288d1', bg: '#e1f5fe', border: '#0288d1' },
  [ChannelType.FACEBOOK]: { main: '#1877f2', bg: '#e3f2fd', border: '#1877f2' },
  [ChannelType.INSTAGRAM]: { main: '#e1306c', bg: '#fce4ec', border: '#e1306c' },
  [ChannelType.NOTE]: { main: '#b45309', bg: '#fffbeb', border: '#f59e0b' },
  [ChannelType.SYSTEM]: { main: '#475569', bg: '#f8fafc', border: '#cbd5e1' },
};

const TWILIO_ERROR_REASONS = {
  '63016': 'Blocked',
  '63024': 'Not on WhatsApp',
  '63032': 'Session expired',
  '63033': 'No active session',
  '30003': 'Unreachable',
  '30006': 'Landline',
};


const MESSAGE_FILTER_LIST = [
  { key: '', label: 'All', Icon: ChatIcon },
  { key: ChannelType.EMAIL, label: 'Email', Icon: EmailIcon },
  { key: ChannelType.PHONE, label: 'Phone', Icon: PhoneIcon },
  { key: ChannelType.WHATSAPP, label: 'WhatsApp', Icon: WhatsAppIcon },
  { key: ChannelType.SMS, label: 'SMS', Icon: SmsIcon },
  { key: ChannelType.FACEBOOK, label: 'Facebook', Icon: FacebookIcon },
  { key: ChannelType.INSTAGRAM, label: 'Instagram', Icon: InstagramIcon },
  { key: ChannelType.NOTE, label: 'Notes', Icon: DescriptionIcon },
  { key: ChannelType.SYSTEM, label: 'System', Icon: MarkChatReadIcon },
];

const CHANNEL_PILL_LIST = [
  { key: ChannelType.EMAIL, label: 'Email', Icon: EmailIcon },
  { key: ChannelType.WHATSAPP, label: 'WhatsApp', Icon: WhatsAppIcon },
  { key: ChannelType.SMS, label: 'SMS', Icon: SmsIcon },
  { key: ChannelType.FACEBOOK, label: 'Facebook', Icon: FacebookIcon },
  { key: ChannelType.INSTAGRAM, label: 'Instagram', Icon: InstagramIcon },
];

const MESSAGE_STATUS_OPTIONS = [
  { key: '', label: 'No status' },
  { key: 'unread', label: 'Unread' },
  { key: 'needs_read', label: 'Need to read' },
  { key: 'undone', label: 'Undone' },
  { key: 'important', label: 'Important' },
  { key: 'ignored', label: 'Ignored' },
  { key: 'answered', label: 'Done' },
  { key: 'task_open', label: 'Task open' },
  { key: 'task_closed', label: 'Task closed' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'spam', label: 'Spam' },
  { key: 'blocked', label: 'Blocked' },
];

const MESSAGE_TAG_OPTIONS = [
  'services lead',
  'service done',
  'repair',
  'buy',
  'recycle',
  'sell',
];

const CONTACT_CATEGORY_OPTIONS = [
  'supplier',
  'finance',
  'legal',
  'customer',
  'marketing',
  'new customer',
  'old customer',
  'developer',
  'club member',
  'mechanics',
  'drop off',
  'driver',
  'existing repair id',
];

const statusLabel = (value) => (
  MESSAGE_STATUS_OPTIONS.find((item) => item.key === value)?.label || value
);


const ConversationView = ({
  conversationId,
  initialMessageId = null,
  onBack,
  initialChannel = null,
  workToolsRequest = 0,
  mobileFullScreen = false,
  onConversationUpdated = null,
  emailOnly = false,
  embedded = false, // fill the parent container (e.g. incoming-call popup)
  onOpenConversation = null,
}) => {
  const at = useAutoTranslate();
  const { user, isAdmin, hasPermission } = useAuth();
  const canSend = isAdmin || hasPermission('send_communication');
  const canDial = isAdmin || hasPermission('dial_communication');
  const canReadTasks = isAdmin || hasPermission('work_tasks.read');
  const navigate = useNavigate();
  const [conversation, setConversation] = useState(null);
  const [mailPolicy, setMailPolicy] = useState(null);
  const [mailPolicyError, setMailPolicyError] = useState(false);
  useEffect(() => {
    let active = true;
    setMailPolicy(null);
    setMailPolicyError(false);
    if (isAdmin && conversationId) {
      getConversationMailPolicy(conversationId)
        .then((policy) => { if (active) setMailPolicy(policy); })
        .catch(() => { if (active) setMailPolicyError(true); });
    }
    return () => { active = false; };
  }, [conversationId, isAdmin]);
  const [taskSource, setTaskSource] = useState(null);
  const [anchoredMessage, setAnchoredMessage] = useState(null);
  const [anchorError, setAnchorError] = useState('');
  useEffect(() => {
    let active = true;
    setAnchoredMessage(null); setAnchorError(''); setTaskSource(null);
    if (initialMessageId && conversationId) getConversationMessageAnchor(conversationId, initialMessageId)
      .then(message => { if (active) setAnchoredMessage(message); })
      .catch(() => { if (active) setAnchorError('The original message is not available or no longer shared.'); });
    return () => { active = false; };
  }, [conversationId, initialMessageId]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [suggestingReply, setSuggestingReply] = useState(false);
  const [proofreading, setProofreading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [groupParticipants, setGroupParticipants] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedTemplateMeta, setSelectedTemplateMeta] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(ChannelType.EMAIL);
  const [channelAutoDetected, setChannelAutoDetected] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [selectedTranscript, setSelectedTranscript] = useState(null);
  const [showRecordingDialog, setShowRecordingDialog] = useState(false);
  const [selectedCallRecording, setSelectedCallRecording] = useState(null);
  const [transcribingCallId, setTranscribingCallId] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showCallModal, setShowCallModal] = useState(false);
  const [closingConversation, setClosingConversation] = useState(false);
  const [showCloseConversationConfirm, setShowCloseConversationConfirm] = useState(false);
  const [markingRead, setMarkingRead] = useState(false);
  const [quickStatusUpdating, setQuickStatusUpdating] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [reactionAnchor, setReactionAnchor] = useState(null);
  const [reactionMessage, setReactionMessage] = useState(null);
  const [savingReaction, setSavingReaction] = useState(false);
  const [messageChannelFilter, setMessageChannelFilter] = useState('');
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [templateAnchor, setTemplateAnchor] = useState(null);
  const [templateSearch, setTemplateSearch] = useState('');
  const [twilioTemplates, setTwilioTemplates] = useState([]);
  const [twilioTemplatesLoaded, setTwilioTemplatesLoaded] = useState(false);
  const [twilioTemplatesLoading, setTwilioTemplatesLoading] = useState(false);
  const [twilioTemplatesError, setTwilioTemplatesError] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [recording, setRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const [processingAudio, setProcessingAudio] = useState(false);
  const [transcribingAudio, setTranscribingAudio] = useState(false);
  const [aiDraftLoading, setAiDraftLoading] = useState(false);
  const [aiFeedbackCategory, setAiFeedbackCategory] = useState(null); // structured feedback on the AI draft
  const [aiDraft, setAiDraft] = useState(null);
  const [showLearningRecsModal, setShowLearningRecsModal] = useState(false);
  const [learningRecs, setLearningRecs] = useState([]);
  const [loadingRecs, setLoadingRecs] = useState(false);

  // Older bot messages were stored as "[WhatsApp-Vorlage: name]". Load the
  // approved templates once so the real text can be shown instead.
  const hasTemplatePlaceholders = useMemo(
    () => messages.some((msg) => templatePlaceholderName(msg.content)),
    [messages],
  );
  useEffect(() => {
    if (!hasTemplatePlaceholders || twilioTemplatesLoaded || twilioTemplatesLoading) return undefined;
    let cancelled = false;
    getTwilioWhatsAppTemplates()
      .then((response) => {
        if (cancelled) return;
        setTwilioTemplates(Array.isArray(response?.items) ? response.items : []);
        setTwilioTemplatesLoaded(true);
      })
      .catch(() => { if (!cancelled) setTwilioTemplatesLoaded(true); });
    return () => { cancelled = true; };
  }, [hasTemplatePlaceholders, twilioTemplatesLoaded, twilioTemplatesLoading]);

  const templateTextByName = useMemo(() => {
    const map = {};
    twilioTemplates.forEach((item) => {
      const name = item.whatsappTemplateName || item.label;
      const body = item.messages?.[item.language] || Object.values(item.messages || {})[0];
      if (name && body && !map[name]) map[name] = body;
    });
    return map;
  }, [twilioTemplates]);

  useEffect(() => {
    if (!templateAnchor || selectedChannel !== ChannelType.WHATSAPP) return undefined;
    let cancelled = false;
    setTwilioTemplatesLoading(true);
    setTwilioTemplatesError('');
    getTwilioWhatsAppTemplates()
      .then((response) => {
        if (cancelled) return;
        setTwilioTemplates(Array.isArray(response?.items) ? response.items : []);
        setTwilioTemplatesLoaded(true);
      })
      .catch((err) => {
        if (cancelled) return;
        const detail = err?.response?.data?.detail;
        setTwilioTemplatesError(
          detail || 'Twilio templates could not be loaded. Local templates are shown for now.'
        );
        setTwilioTemplatesLoaded(false);
      })
      .finally(() => {
        if (!cancelled) setTwilioTemplatesLoading(false);
      });
    return () => { cancelled = true; };
  }, [templateAnchor, selectedChannel]);

  const handleGenerateAiDraft = async () => {
    if (!conversationId) return;
    try {
      setAiDraftLoading(true);
      setError(null);
      const data = await generateAiResponseDraft({
        conversation_id: conversationId,
        channel: selectedChannel || ChannelType.WHATSAPP
      });
      setAiDraft(data);
    } catch (err) {
      console.error('Error generating AI draft:', err);
      setError(formatApiError(err, 'Failed to generate AI response suggestion'));
    } finally {
      setAiDraftLoading(false);
    }
  };

  const handleApplyAiDraft = () => {
    if (!aiDraft?.ai_draft_text) return;
    setNewMessage(aiDraft.ai_draft_text);
    setNotice({ severity: 'success', message: '🤖 AI suggestion inserted into composer!' });
  };

  const handleOpenLearningRecs = async () => {
    setShowLearningRecsModal(true);
    setLoadingRecs(true);
    try {
      const data = await getLearningRecommendations('PENDING_REVIEW');
      setLearningRecs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.warn('Error loading learning recommendations:', err);
    } finally {
      setLoadingRecs(false);
    }
  };

  const handleApproveRec = async (recId) => {
    try {
      await approveLearningRecommendation(recId, user?.id || 'admin');
      setLearningRecs(prev => prev.filter(r => r.id !== recId));
      setNotice({ severity: 'success', message: '✅ Recommendation approved and integrated!' });
    } catch (err) {
      console.error('Failed to approve recommendation:', err);
      setError('Failed to approve recommendation');
    }
  };
  const [transcribingAttachmentKey, setTranscribingAttachmentKey] = useState('');
  const [audioReviewOpen, setAudioReviewOpen] = useState(false);
  const [audioReviewLanguage, setAudioReviewLanguage] = useState(() => (
    localStorage.getItem(TEMPLATE_LANGUAGE_STORAGE_KEY) || 'de'
  ));
  const [audioReview, setAudioReview] = useState({
    original: '',
    enhanced: '',
    sourceLanguage: '',
    targetLanguage: '',
  });
  const translationLanguage = getAccountLanguage(user);
  // Replies written in this account's language keep their wording in metadata;
  // they need no AI round trip to be shown in the agent's language.
  const liveTranslationMessages = useMemo(
    () => messages.filter((msg) => !ownTranslatedText(msg, translationLanguage)),
    [messages, translationLanguage],
  );
  const {
    inlineTranslation, hasTranslations, translating: translatingConversation,
    translationError, showOriginal, toggleOriginal, retryTranslation,
  } = useLiveTranslation(conversationId, liveTranslationMessages, translationLanguage, user?.id);
  const [showConversationTriage, setShowConversationTriage] = useState(false);
  const [triageForm, setTriageForm] = useState({
    status: '',
    tags: [],
    contact_categories: [],
    task_status: '',
    task_title: '',
    task_note: '',
    repair_id: '',
    process_id: '',
    apply_repair_to_conversation: true,
  });
  const [savingTriage, setSavingTriage] = useState(false);
  const [repairSearch, setRepairSearch] = useState('');
  const [repairOptions, setRepairOptions] = useState([]);
  const [repairLookupLoading, setRepairLookupLoading] = useState(false);
  const [repairLookupError, setRepairLookupError] = useState('');
  const [actorDialogOpen, setActorDialogOpen] = useState(false);
  const [translationOutage, setTranslationOutage] = useState(null);
  const [customerBarKey, setCustomerBarKey] = useState(0);
  const [templateLanguage, setTemplateLanguage] = useState(() => (
    localStorage.getItem(TEMPLATE_LANGUAGE_STORAGE_KEY) || 'de'
  ));
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const pendingAttachmentsRef = useRef([]);
  const mediaRecorderRef = useRef(null);
  const recordingStreamRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const recordingSecondsRef = useRef(0);
  const recordedAudioRef = useRef(null);
  const messagesSignatureRef = useRef('');
  const messagesContainerRef = useRef(null);
  const isAtBottomRef = useRef(true);
  const shouldForceScrollRef = useRef(true);
  // Guards against a stale async response from a previously-open conversation
  // overwriting the one now selected (the "stuck on the old chat" bug).
  const latestConvIdRef = useRef(null);
  latestConvIdRef.current = conversationId;

  const openEmailModal = (msg) => {
    setSelectedEmail(msg);
    setShowEmailModal(true);
  };

  const openCallRecordingDialog = (msg) => {
    setSelectedCallRecording(msg);
    setShowRecordingDialog(true);
  };

  const openTranscriptDialog = (msg) => {
    setSelectedTranscript(msg);
    setShowTranscript(true);
  };

  const handleTranscribeCallRecording = async (msg) => {
    const callRecordId = msg?.call_record_id || msg?.message_metadata?.call_record_id;
    if (!callRecordId || transcribingCallId) return;

    try {
      setTranscribingCallId(String(callRecordId));
      setError(null);
      const result = await transcribeCallRecording(callRecordId, Boolean(msg.transcript_text));
      const transcriptText = result?.transcript_text || '';
      const updatedMessage = {
        ...msg,
        has_transcript: Boolean(transcriptText),
        transcript_text: transcriptText,
        transcript_segments: result?.transcript_segments || [],
        message_metadata: {
          ...(msg.message_metadata || {}),
          transcript_text: transcriptText,
          transcript_segments: result?.transcript_segments || [],
          transcript_language: result?.language,
        },
      };
      setSelectedCallRecording(updatedMessage);
      setSelectedTranscript(updatedMessage);
      shouldForceScrollRef.current = false;
      await fetchConversation(messageChannelFilter);
    } catch (err) {
      console.error('Error transcribing call recording:', err);
      setError(formatApiError(err, 'Failed to transcribe call recording'));
    } finally {
      setTranscribingCallId('');
    }
  };

  // Load scheduled messages for conversation
  const fetchScheduledMessages = useCallback(async () => {
    if (!conversationId) return;
    try {
      const data = await getScheduledMessages(conversationId, 'PENDING');
      setScheduledMessages(Array.isArray(data) ? data : []);
    } catch (err) {
      console.warn('Error fetching scheduled messages:', err);
    }
  }, [conversationId]);

  useEffect(() => {
    fetchScheduledMessages();
  }, [conversationId, fetchScheduledMessages]);

  const handleScheduleSubmit = async () => {
    if (!newMessage.trim() && pendingAttachments.length === 0) return;
    if (!plannedDateTime) {
      setError('Please select a planned date and time.');
      return;
    }

    const scheduledDate = new Date(plannedDateTime);
    if (isNaN(scheduledDate.getTime()) || scheduledDate <= new Date()) {
      setError('Please select a date & time in the future.');
      return;
    }

    try {
      setScheduling(true);
      setError(null);
      
      const channelForSend = selectedChannel || ChannelType.EMAIL;
      const senderDisplay = user?.first_name ? `Agent (${user.first_name})` : 'Agent';

      await scheduleMessage({
        conversation_id: conversationId,
        channel: channelForSend,
        sender_display: senderDisplay,
        subject: 'Message from eScooterLab',
        content: newMessage.trim(),
        content_html: `<p>${newMessage.trim().replace(/\n/g, '<br/>')}</p>`,
        scheduled_at: scheduledDate.toISOString(),
        attachments: [],
      });

      setNewMessage('');
      setPendingAttachments([]);
      setSchedulePickerOpen(false);
      setPlannedDateTime('');
      setNotice({
        severity: 'success',
        message: `Message scheduled for ${formatDDMMYYYY(scheduledDate)} via ${channelForSend}.`,
      });
      await fetchScheduledMessages();
    } catch (err) {
      console.error('Error scheduling message:', err);
      setError(formatApiError(err, 'Failed to schedule message'));
    } finally {
      setScheduling(false);
    }
  };

  const handleCancelScheduledMessage = async (scheduledId) => {
    try {
      await cancelScheduledMessage(scheduledId);
      setNotice({ severity: 'info', message: 'Scheduled message cancelled.' });
      await fetchScheduledMessages();
    } catch (err) {
      console.error('Error cancelling scheduled message:', err);
      setError(formatApiError(err, 'Failed to cancel scheduled message'));
    }
  };

  const scrollToBottom = (force = false) => {
    const el = messagesEndRef.current;
    if (el && (force || isAtBottomRef.current)) {
      // Use setTimeout to ensure the browser has finished layout calculations and rendering
      setTimeout(() => {
        el.scrollIntoView({
          behavior: force ? 'auto' : 'smooth',
          block: 'end'
        });
      }, 50);
    }
  };

  const handleMessagesScroll = useCallback((e) => {
    const el = e.currentTarget;
    isAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  }, []);

  const setMessagesFromServer = useCallback((messagesData) => {
    const nextMessages = Array.isArray(messagesData) ? messagesData : [];
    const nextSignature = getMessagesSignature(nextMessages);
    if (nextSignature !== messagesSignatureRef.current) {
      messagesSignatureRef.current = nextSignature;
      setMessages(nextMessages);
      return true;
    }
    return false;
  }, []);

  const fetchConversation = async (channelFilter = '') => {
    const reqConvId = conversationId;
    shouldForceScrollRef.current = true;
    try {
      setLoading(true);
      setError(null);

      const [convData, messagesData] = await Promise.all([
        getConversation(conversationId),
        getConversationMessages(conversationId, 50, 0, channelFilter || null)
      ]);

      // Drop the result if the user has since switched to another conversation.
      if (reqConvId !== latestConvIdRef.current) return;

      setConversation(convData);
      setMessagesFromServer(messagesData);

      // Auto-detect reply channel from conversation's last channel
      if (convData?.last_channel && !channelAutoDetected && !emailOnly) {
        const lc = convData.last_channel;
        if ([ChannelType.EMAIL, ChannelType.WHATSAPP, ChannelType.SMS, ChannelType.FACEBOOK, ChannelType.INSTAGRAM].includes(lc)) {
          setSelectedChannel(lc);
        }
        setChannelAutoDetected(true);
      }

      // Mark as read
      if (user?.id) {
        await markConversationAsRead(conversationId, user.id);
      }
    } catch (err) {
      if (reqConvId !== latestConvIdRef.current) return;
      console.error('Error fetching conversation:', err);
      setError('Failed to load conversation');
    } finally {
      if (reqConvId === latestConvIdRef.current) setLoading(false);
    }
  };

  // Refresh the thread WITHOUT flipping the full-screen loading spinner, so
  // sending a message updates the thread in place instead of visibly reloading.
  const silentRefresh = async (channelFilter = '') => {
    const reqConvId = conversationId;
    try {
      const [convData, messagesData] = await Promise.all([
        getConversation(conversationId),
        getConversationMessages(conversationId, 50, 0, channelFilter || null),
      ]);
      if (reqConvId !== latestConvIdRef.current) return;
      setConversation(convData);
      setMessagesFromServer(messagesData);
      if (user?.id) {
        try { await markConversationAsRead(conversationId, user.id); } catch (_) { /* best-effort read receipt */ }
      }
    } catch (err) {
      // Keep the current thread on screen; the next poll will retry.
      console.warn('Silent refresh failed:', err);
    }
  };

  const fetchMessagesForFilter = async (channelFilter) => {
    if (!conversationId) return;
    shouldForceScrollRef.current = true;
    try {
      setMessagesLoading(true);
      setError(null);
      const messagesData = await getConversationMessages(conversationId, 50, 0, channelFilter || null);
      setMessagesFromServer(messagesData);
    } catch (err) {
      console.error('Error filtering conversation messages:', err);
      setError('Failed to filter messages');
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleManualMarkAsRead = async () => {
    if (!conversationId || markingRead) return;

    setMenuAnchor(null);
    if (!user?.id) {
      setError('Cannot mark conversation as read until the user session is loaded.');
      return;
    }

    setMarkingRead(true);
    setError(null);

    try {
      await markConversationAsRead(conversationId, user?.id);
      setConversation((prev) => prev ? { ...prev, unread_count: 0 } : prev);
      setMessages((prev) => prev.map((message) => ({ ...message, is_read: true })));
      if (onConversationUpdated) onConversationUpdated();
    } catch (err) {
      console.error('Failed to mark conversation as read:', err);
      setError(formatApiError(err, 'Failed to mark conversation as read'));
    } finally {
      setMarkingRead(false);
    }
  };

  const handleQuickConversationStatus = async (statusValue) => {
    if (!conversationId || quickStatusUpdating) return;

    setMenuAnchor(null);
    setQuickStatusUpdating(statusValue);
    setError(null);

    try {
      const updated = await updateConversationTriage(conversationId, { status: statusValue });
      setConversation(updated);
      if (['answered', 'ignored', 'cancelled', 'canceled', 'task_closed'].includes(statusValue)) {
        setMessages((prev) => prev.map((message) => ({ ...message, is_read: true })));
      }
      if (onConversationUpdated) onConversationUpdated();
    } catch (err) {
      console.error('Failed to update conversation status:', err);
      setError(formatApiError(err, 'Failed to update conversation status'));
    } finally {
      setQuickStatusUpdating('');
    }
  };

  const requestCloseConversation = () => {
    setMenuAnchor(null);
    setShowCloseConversationConfirm(true);
  };

  const confirmCloseConversation = async () => {
    if (!conversationId || closingConversation) return;

    setClosingConversation(true);
    setError(null);

    try {
      await closeConversation(conversationId);
      setShowCloseConversationConfirm(false);
      if (onConversationUpdated) onConversationUpdated();
      onBack();
    } catch (err) {
      console.error('Failed to close conversation:', err);
      setError(formatApiError(err, 'Failed to close conversation'));
    } finally {
      setClosingConversation(false);
    }
  };

  const handleMessageFilterChange = (filterKey) => {
    setMessageChannelFilter(filterKey);
    fetchMessagesForFilter(filterKey);
  };

  const openReactionMenu = (event, msg) => {
    event.stopPropagation();
    setReactionAnchor(event.currentTarget);
    setReactionMessage(msg);
  };

  const closeReactionMenu = () => {
    if (savingReaction) return;
    setReactionAnchor(null);
    setReactionMessage(null);
  };

  const applyMessageReaction = async (emoji, remove = false) => {
    if (!conversationId || !reactionMessage || savingReaction) return;
    setSavingReaction(true);
    setError(null);

    try {
      const updated = await setMessageReaction(conversationId, reactionMessage.id, { emoji, remove });
      setMessages((prev) => prev.map((message) => (
        message.id === updated.id ? { ...message, ...updated } : message
      )));
      messagesSignatureRef.current = '';
      setReactionAnchor(null);
      setReactionMessage(null);
      if (onConversationUpdated) onConversationUpdated();
    } catch (err) {
      console.error('Failed to react to message:', err);
      setError(formatApiError(err, 'Failed to save message reaction'));
    } finally {
      setSavingReaction(false);
    }
  };

  // The chatbot could not answer: show it until a person has replied.
  const needsHumanReply = useMemo(() => {
    const flag = conversation?.conversation_metadata?.needs_human_reply;
    if (!flag?.since) return null;
    const toMs = (value) => {
      const text = String(value || '');
      const time = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(text) ? text : `${text}Z`).getTime();
      return Number.isNaN(time) ? 0 : time;
    };
    const since = toMs(flag.since);
    const answered = messages.some((msg) => String(msg.direction).toUpperCase() === 'OUTBOUND'
      && !msg.message_metadata?.chatbot
      && !['failed', 'undelivered'].includes(msg.delivery_status)
      && toMs(msg.created_at) > since);
    return answered ? null : flag;
  }, [conversation?.conversation_metadata?.needs_human_reply, messages]);

  const customerNotOnWhatsApp = useMemo(() =>
    messages.some(
      (msg) => msg.channel === ChannelType.WHATSAPP
        && String(msg.direction).toUpperCase() === 'OUTBOUND'
        && msg.delivery_status === 'undelivered'
        && ['63016', '63024', '63032', '63033'].includes(String(msg.message_metadata?.twilio_error_code || ''))
    ), [messages]);

  const hasUnresolvedTwilioDelivery = useMemo(() => {
    const unresolvedStatuses = new Set(['queued', 'accepted', 'sending', 'scheduled', 'sent']);
    const freshnessMs = 15 * 60 * 1000;

    return messages.some((msg) => {
      const isOutbound = String(msg.direction).toUpperCase() === 'OUTBOUND';
      const isTwilioChannel = [ChannelType.WHATSAPP, ChannelType.SMS].includes(msg.channel);
      const status = String(msg.delivery_status || '').toLowerCase();
      if (!isOutbound || !isTwilioChannel || !unresolvedStatuses.has(status) || !msg.message_metadata?.twilio_sid) {
        return false;
      }

      const createdAt = Date.parse(msg.created_at || '');
      return !Number.isNaN(createdAt) && Date.now() - createdAt < freshnessMs;
    });
  }, [messages]);

  useEffect(() => {
    if (conversationId) {
      messagesSignatureRef.current = '';
      // Clear the previous conversation's content immediately so switching never
      // shows the old chat while the new one loads.
      setMessages([]);
      setConversation(null);
      setChannelAutoDetected(false);
      setMessageChannelFilter('');
      setNewMessage('');
      setSelectedTemplateMeta(null);
      setProofreading(false);
      setNotice(null);
      if ([ChannelType.EMAIL, ChannelType.WHATSAPP, ChannelType.SMS, ChannelType.FACEBOOK, ChannelType.INSTAGRAM].includes(initialChannel)) {
        setSelectedChannel(initialChannel);
      }
      fetchConversation('');
    }
  }, [conversationId, initialChannel]);

  // Load the anonymized participant list for group conversations.
  useEffect(() => {
    let cancelled = false;
    if (conversationId && conversation?.is_group) {
      getGroupParticipants(conversationId)
        .then((list) => { if (!cancelled) setGroupParticipants(Array.isArray(list) ? list : []); })
        .catch(() => { if (!cancelled) setGroupParticipants([]); });
    } else {
      setGroupParticipants([]);
    }
    return () => { cancelled = true; };
  }, [conversationId, conversation?.is_group]);

  useEffect(() => {
    if (!conversationId) return undefined;

    let cancelled = false;

    const applyMessages = async (messagesData) => {
      if (cancelled) return;
      const changed = setMessagesFromServer(messagesData);
      // Safety net: if an initial load hung (e.g. a 502), any successful
      // refresh clears the spinner so the chat recovers on its own.
      setLoading(false);
      if (changed && user?.id) {
        try { await markConversationAsRead(conversationId, user.id); } catch (_) { /* best-effort read receipt */ }
      }
    };

    const refreshOpenMessages = async () => {
      if (cancelled || document.hidden) return;
      try {
        const messagesData = await getConversationMessages(
          conversationId,
          50,
          0,
          messageChannelFilter || null,
        );
        await applyMessages(messagesData);
      } catch (err) {
        console.warn('Live message refresh failed:', err);
      }
    };

    const refreshOpenConversation = async () => {
      if (cancelled || document.hidden) return;
      try {
        const [convData, messagesData] = await Promise.all([
          getConversation(conversationId),
          getConversationMessages(conversationId, 50, 0, messageChannelFilter || null),
        ]);
        if (cancelled) return;
        setConversation(convData);
        await applyMessages(messagesData);
      } catch (err) {
        console.warn('Live conversation refresh failed:', err);
      }
    };

    const handleVisibilityRefresh = () => {
      if (!document.hidden) {
        refreshOpenConversation();
      }
    };

    // Keep the existing fast message cadence so inbound chat freshness is
    // unchanged, but avoid re-fetching conversation metadata on every tick.
    // Metadata is refreshed immediately on focus, visibility return and local
    // communication refresh events.
    const refreshEveryMs = hasUnresolvedTwilioDelivery ? 2500 : 3000;
    const interval = window.setInterval(refreshOpenMessages, refreshEveryMs);
    window.addEventListener('communication-tunnel-refresh', refreshOpenConversation);
    window.addEventListener('focus', refreshOpenConversation);
    document.addEventListener('visibilitychange', handleVisibilityRefresh);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('communication-tunnel-refresh', refreshOpenConversation);
      window.removeEventListener('focus', refreshOpenConversation);
      document.removeEventListener('visibilitychange', handleVisibilityRefresh);
    };
  }, [conversationId, messageChannelFilter, user?.id, setMessagesFromServer, hasUnresolvedTwilioDelivery]);

  useEffect(() => {
    if (!loading) {
      if (shouldForceScrollRef.current) {
        scrollToBottom(true);
        shouldForceScrollRef.current = false;
      } else {
        scrollToBottom();
      }
    }
  }, [messages, messageChannelFilter, loading]);

  useEffect(() => {
    if (conversation?.id === conversationId) {
      setTemplateLanguage(conversation.customer_language || 'de');
    }
  }, [conversationId, conversation?.id, conversation?.customer_language]);

  useEffect(() => {
    pendingAttachmentsRef.current = pendingAttachments;
  }, [pendingAttachments]);

  useEffect(() => {
    recordedAudioRef.current = recordedAudio;
  }, [recordedAudio]);

  useEffect(() => {
    if (workToolsRequest > 0 && conversation && !loading) {
      openConversationTriageDialog();
    }
  }, [workToolsRequest, conversation?.id, loading]);

  useEffect(() => () => {
    pendingAttachmentsRef.current.forEach((item) => {
      if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
    });
    if (recordingTimerRef.current) {
      window.clearInterval(recordingTimerRef.current);
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (recordingStreamRef.current?.getTracks) {
      recordingStreamRef.current.getTracks().forEach((track) => track.stop());
    }
    if (recordedAudioRef.current?.previewUrl) {
      URL.revokeObjectURL(recordedAudioRef.current.previewUrl);
    }
  }, []);

  const shownMessages = messageChannelFilter
    ? messages.filter((msg) => msg.channel === messageChannelFilter)
    : messages;

  const activeFilterLabel = MESSAGE_FILTER_LIST.find((item) => item.key === messageChannelFilter)?.label || 'messages';
  const templateLanguageLabel = TEMPLATE_LANGUAGES.find((item) => item.key === templateLanguage)?.label || 'DE';
  const audioReviewLanguageOption = AI_LANGUAGE_OPTIONS.find((item) => item.key === audioReviewLanguage) || AI_LANGUAGE_OPTIONS[0];
  const translationLanguageOption = AI_LANGUAGE_OPTIONS.find((item) => item.key === translationLanguage) || AI_LANGUAGE_OPTIONS[0];
  const inlineTranslationsById = useMemo(() => {
    const items = inlineTranslation?.messages || [];
    return items.reduce((acc, item) => {
      if (item?.message_id) acc[item.message_id] = item;
      return acc;
    }, {});
  }, [inlineTranslation]);
  const inlineTranslationActive = Boolean(inlineTranslation?.languageKey && inlineTranslation?.messages?.length);

  // Scheduled / Planned Messages State
  const [scheduledMessages, setScheduledMessages] = useState([]);
  const [showScheduledDialog, setShowScheduledDialog] = useState(false);
  const [schedulePickerOpen, setSchedulePickerOpen] = useState(false);
  const [plannedDateTime, setPlannedDateTime] = useState(() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() + 5);
    return getLocalISOString(d);
  });
  const [scheduling, setScheduling] = useState(false);
  const getAudioLanguageName = (key) => (
    AI_LANGUAGE_OPTIONS.find((item) => item.key === key)?.name || key || 'Original language'
  );
  const normalizedTemplateSearch = templateSearch.trim().toLowerCase();

  const getTemplateBody = (template) => (
    template?.messages?.[templateLanguage]
    || template?.messages?.de
    || Object.values(template?.messages || {})[0]
    || ''
  );

  const pickerTemplates = (
    selectedChannel === ChannelType.WHATSAPP && twilioTemplatesLoaded
      ? twilioTemplates
      : MESSAGE_TEMPLATES
  );

  const filteredTemplates = useMemo(() => {
    const languageMatches = pickerTemplates.filter((template) => (
      !template.language || template.language === templateLanguage
    ));
    const source = languageMatches.length ? languageMatches : pickerTemplates;
    if (!normalizedTemplateSearch) return source;

    return source.filter((template) => {
      const haystack = [
        template.label,
        template.command,
        ...(template.aliases || []),
        template?.messages?.[templateLanguage] || template?.messages?.de || '',
      ].join(' ').toLowerCase();

      return haystack.includes(normalizedTemplateSearch);
    });
  }, [normalizedTemplateSearch, templateLanguage, pickerTemplates]);

  const slashTemplateMatches = useMemo(() => {
    const draft = newMessage.trim();
    if (!draft.startsWith('/')) return [];
    return findTemplateCommandMatches(draft).slice(0, 6);
  }, [newMessage]);

  const applyTemplate = (template) => {
    const body = getTemplateBody(template);
    if (!body) return;

    setNewMessage(body);
    setSelectedTemplateMeta({
      key: template.key,
      label: template.label,
      whatsappTemplateName: template.whatsappTemplateName || template.key,
      language: template.language || templateLanguage,
      contentSid: template.contentSid || null,
      category: template.whatsappCategory || '',
      contentType: template.contentType || '',
      approvalStatus: template.approvalStatus || '',
      body,
    });
    setTemplateAnchor(null);
    setTemplateSearch('');
    requestAnimationFrame(() => {
      if (messageInputRef.current) {
        messageInputRef.current.focus();
      }
    });
  };

  const handleNewMessageChange = (value) => {
    setNewMessage(value);
    setSelectedTemplateMeta((current) => (
      current && value !== current.body ? null : current
    ));
  };

  const handleProofread = async () => {
    if (!newMessage.trim() || proofreading) return;
    try {
      setProofreading(true);
      setError(null);
      const result = await proofreadText(newMessage, conversationId);
      const corrected = (result?.corrected_text || '').trim();
      if (corrected) {
        setNewMessage(corrected);
        setSelectedTemplateMeta(null);
        requestAnimationFrame(() => messageInputRef.current?.focus?.());
      }
    } catch (err) {
      setError(formatApiError(err, 'Failed to proofread message'));
    } finally {
      setProofreading(false);
    }
  };

  const handleSuggestReply = async () => {
    if (!conversationId || suggestingReply) return;

    try {
      setSuggestingReply(true);
      setError(null);
      const targetLanguageName = AI_LANGUAGE_OPTIONS.find((item) => item.key === translationLanguage)?.name || translationLanguage;
      const result = await suggestConversationReply(conversationId, {
        target_language: targetLanguageName,
        channel: selectedChannel,
        tone: selectedChannel === ChannelType.EMAIL ? 'professional email' : 'friendly professional',
      });
      const suggestedReply = (result?.suggested_reply || '').trim();

      if (!suggestedReply) {
        setError('AI did not return a suggested reply.');
        return;
      }

      setNewMessage(suggestedReply);
      setSelectedTemplateMeta(null);
      requestAnimationFrame(() => messageInputRef.current?.focus?.());
    } catch (err) {
      console.error('Error suggesting reply:', err);
      setError(formatApiError(err, 'Failed to suggest a reply'));
    } finally {
      setSuggestingReply(false);
    }
  };

  const handleTranslateConversation = () => retryTranslation();
  const clearInlineTranslation = () => toggleOriginal();

  const formatFileSize = (size = 0) => {
    if (!size) return '';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const attachmentIcon = (contentType = '') => {
    if (contentType.startsWith('image/')) return ImageIcon;
    if (contentType.startsWith('video/')) return MovieIcon;
    if (contentType.startsWith('audio/')) return VoiceNoteIcon;
    return AttachFileIcon;
  };

  const isImageAttachment = (attachment) => {
    const contentType = String(attachment?.content_type || '').toLowerCase();
    const url = String(attachment?.url || '').toLowerCase();
    return contentType.startsWith('image/') || contentType === 'image' || /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/.test(url);
  };

  const isVideoAttachment = (attachment) => {
    const contentType = String(attachment?.content_type || '').toLowerCase();
    const url = String(attachment?.url || '').toLowerCase();
    return contentType.startsWith('video/') || contentType === 'video' || /\.(mp4|mov|m4v|webm|avi|mkv)(\?|$)/.test(url);
  };

  const isAudioAttachment = (attachment) => {
    const contentType = String(attachment?.content_type || '').toLowerCase();
    const url = String(attachment?.url || '').toLowerCase();
    return contentType.startsWith('audio/') || contentType === 'audio' || /\.(mp3|m4a|wav|ogg|oga|webm|aac)(\?|$)/.test(url);
  };

  const handleAttachmentSelect = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const nextItems = files.map((file) => ({
      id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
      file,
      name: file.name,
      size: file.size,
      contentType: file.type || 'application/octet-stream',
      previewUrl: file.type?.startsWith('image/') || file.type?.startsWith('video/')
        ? URL.createObjectURL(file)
        : null,
    }));

    setPendingAttachments((prev) => [...prev, ...nextItems]);
    event.target.value = '';
  };

  const getRecordingMimeType = () => {
    if (!window.MediaRecorder?.isTypeSupported) return '';
    const candidates = [
      'audio/ogg;codecs=opus',
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
    ];
    return candidates.find((type) => window.MediaRecorder.isTypeSupported(type)) || '';
  };

  const getAudioExtension = (mimeType = '') => {
    const normalized = mimeType.toLowerCase();
    if (normalized.includes('ogg')) return 'ogg';
    if (normalized.includes('mp4')) return 'm4a';
    if (normalized.includes('wav')) return 'wav';
    return 'webm';
  };

  const createAudioAttachmentItem = (file, previewUrl = null, duration = 0) => ({
    id: `voice-${file.name}-${file.size}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    file,
    name: file.name,
    size: file.size,
    contentType: file.type || 'audio/webm',
    previewUrl,
    duration,
    isVoiceNote: true,
  });

  const stopRecordingTimer = () => {
    if (recordingTimerRef.current) {
      window.clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  };

  const stopRecordingStream = () => {
    if (recordingStreamRef.current?.getTracks) {
      recordingStreamRef.current.getTracks().forEach((track) => track.stop());
    }
    recordingStreamRef.current = null;
  };

  const clearRecordedAudio = () => {
    setRecordedAudio((current) => {
      if (current?.previewUrl) URL.revokeObjectURL(current.previewUrl);
      return null;
    });
  };

  const startAudioRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      setError('Voice recording is not supported by this browser.');
      return;
    }

    try {
      clearRecordedAudio();
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = getRecordingMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);

      recordingChunksRef.current = [];
      recordingStreamRef.current = stream;
      mediaRecorderRef.current = recorder;
      recordingSecondsRef.current = 0;
      setRecordingSeconds(0);

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordingChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        stopRecordingTimer();
        setRecording(false);
        stopRecordingStream();

        const finalType = recorder.mimeType || mimeType || 'audio/webm';
        const chunks = recordingChunksRef.current;
        if (!chunks.length) return;

        const blob = new Blob(chunks, { type: finalType });
        const extension = getAudioExtension(finalType);
        const file = new File([blob], `voice-message-${Date.now()}.${extension}`, { type: finalType });
        const previewUrl = URL.createObjectURL(blob);
        const audioId = `recorded-${Date.now()}-${Math.random().toString(36).slice(2)}`;

        setRecordedAudio({
          id: audioId,
          file,
          previewUrl,
          duration: recordingSecondsRef.current,
        });
        transcribeRecordedAudio(file, audioId);
      };

      recorder.start();
      setRecording(true);
      recordingTimerRef.current = window.setInterval(() => {
        recordingSecondsRef.current += 1;
        setRecordingSeconds(recordingSecondsRef.current);
      }, 1000);
    } catch (err) {
      console.error('Error starting voice recording:', err);
      stopRecordingTimer();
      stopRecordingStream();
      setRecording(false);
      setError(formatApiError(err, 'Could not access the microphone'));
    }
  };

  const stopAudioRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
      return;
    }
    stopRecordingTimer();
    stopRecordingStream();
    setRecording(false);
  };

  const transcribeRecordedAudio = async (file, audioId) => {
    if (!file) return null;

    try {
      setTranscribingAudio(true);
      setError(null);
      const result = await transcribeAndEnhanceAudio(file, false);
      const transcript = (result?.transcript || '').trim();

      setRecordedAudio((current) => {
        if (!current || (audioId && current.id !== audioId)) return current;
        return {
          ...current,
          transcript,
          transcriptLanguage: result?.language || '',
          transcriptDuration: result?.duration || current.duration,
        };
      });

      if (!transcript) {
        setError('OpenAI could not detect speech in this recording.');
      }

      return {
        transcript,
        language: result?.language || '',
        duration: result?.duration,
      };
    } catch (err) {
      console.error('Error transcribing voice message:', err);
      setError(formatApiError(err, 'Could not transcribe the voice message'));
      return null;
    } finally {
      setTranscribingAudio(false);
    }
  };

  const getAttachmentTranscript = (msg, attachmentId) => {
    if (!attachmentId) return null;
    const transcripts = msg?.message_metadata?.attachment_transcripts;
    if (!Array.isArray(transcripts)) return null;
    return transcripts.find((item) => String(item?.attachment_id || '') === String(attachmentId)) || null;
  };

  const saveAttachmentTranscriptToMessage = (messageId, transcriptEntry) => {
    if (!messageId || !transcriptEntry?.attachment_id) return;
    setMessages((prev) => prev.map((message) => {
      if (message.id !== messageId) return message;
      const metadata = { ...(message.message_metadata || {}) };
      const existing = Array.isArray(metadata.attachment_transcripts)
        ? metadata.attachment_transcripts.filter((item) => (
          String(item?.attachment_id || '') !== String(transcriptEntry.attachment_id)
        ))
        : [];
      metadata.attachment_transcripts = [...existing, transcriptEntry];
      return { ...message, message_metadata: metadata };
    }));
    messagesSignatureRef.current = '';
  };

  const handleTranscribeAttachment = async (msg, attachment) => {
    const attachmentId = attachment?.attachment_id;
    if (!conversationId || !msg?.id || !attachmentId) {
      setError('This audio file cannot be transcribed because it was not saved as a local attachment.');
      return;
    }

    const key = `${msg.id}-${attachmentId}`;
    try {
      setTranscribingAttachmentKey(key);
      setError(null);
      const result = await transcribeMessageAudioAttachment(conversationId, msg.id, attachmentId);
      saveAttachmentTranscriptToMessage(msg.id, result);
    } catch (err) {
      console.error('Error transcribing audio attachment:', err);
      setError(formatApiError(err, 'Could not transcribe this vocal message'));
    } finally {
      setTranscribingAttachmentKey('');
    }
  };

  const applyAudioTextToComposer = (text) => {
    const cleanText = (text || '').trim();
    if (!cleanText) return;

    setNewMessage((prev) => {
      const existing = prev.trim();
      return existing ? `${existing}\n\n${cleanText}` : cleanText;
    });
    setSelectedTemplateMeta(null);
    setAudioReviewOpen(false);
    clearRecordedAudio();
    requestAnimationFrame(() => messageInputRef.current?.focus?.());
  };

  const enhanceAudioReviewText = async (
    originalText = audioReview.original,
    sourceLanguage = audioReview.sourceLanguage,
    targetLanguage = audioReviewLanguage
  ) => {
    const cleanText = (originalText || '').trim();
    if (!cleanText) return;

    try {
      setProcessingAudio(true);
      setError(null);
      const targetOption = AI_LANGUAGE_OPTIONS.find((item) => item.key === targetLanguage);
      const result = await enhanceTranscribedAudioText({
        text: cleanText,
        sourceLanguage,
        targetLanguage: targetOption?.name || targetLanguage,
      });
      const enhanced = (result?.enhanced_text || cleanText).trim();

      setAudioReview((current) => ({
        ...current,
        original: cleanText,
        enhanced,
        sourceLanguage: sourceLanguage || result?.language || '',
        targetLanguage,
      }));
      setRecordedAudio((current) => current ? {
        ...current,
        enhancedText: enhanced,
        enhancedLanguage: targetLanguage,
      } : current);
    } catch (err) {
      console.error('Error enhancing voice message:', err);
      setError(formatApiError(err, 'Could not enhance or translate the voice message'));
    } finally {
      setProcessingAudio(false);
    }
  };

  const handleTranscribeRecordedAudio = async () => {
    if (!recordedAudio?.file || processingAudio || transcribingAudio) return;

    let originalText = (recordedAudio.transcript || '').trim();
    let sourceLanguage = recordedAudio.transcriptLanguage || '';

    if (!originalText) {
      const result = await transcribeRecordedAudio(recordedAudio.file, recordedAudio.id);
      originalText = (result?.transcript || '').trim();
      sourceLanguage = result?.language || sourceLanguage;
    }

    if (!originalText) return;

    const targetLanguage = audioReviewLanguage || templateLanguage || 'de';
    setAudioReviewLanguage(targetLanguage);
    setAudioReview({
      original: originalText,
      enhanced: recordedAudio.enhancedLanguage === targetLanguage ? (recordedAudio.enhancedText || '') : '',
      sourceLanguage,
      targetLanguage,
    });
    setAudioReviewOpen(true);

    if (recordedAudio.enhancedLanguage !== targetLanguage || !recordedAudio.enhancedText) {
      await enhanceAudioReviewText(originalText, sourceLanguage, targetLanguage);
    }
  };

  const handleAudioReviewLanguageChange = (_, option) => {
    const nextLanguage = option?.key || 'de';
    setAudioReviewLanguage(nextLanguage);
    if (audioReview.original) {
      enhanceAudioReviewText(audioReview.original, audioReview.sourceLanguage, nextLanguage);
    }
  };

  const removePendingAttachment = (attachmentId) => {
    setPendingAttachments((prev) => {
      const item = prev.find((entry) => entry.id === attachmentId);
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((entry) => entry.id !== attachmentId);
    });
  };

  const uploadPendingAttachments = async (attachments = pendingAttachments) => {
    if (!attachments.length) return [];
    return Promise.all(
      attachments.map((item) => uploadConversationAttachment(conversationId, item.file))
    );
  };

  const getConversationTriage = () => (
    conversation?.conversation_triage
    || conversation?.conversation_metadata?.triage
    || {}
  );

  const applyRepairCandidate = (option) => {
    if (!option) return;
    setTriageForm((prev) => ({
      ...prev,
      repair_id: option.repair_id || prev.repair_id,
      process_id: option.process_id || prev.process_id,
      apply_repair_to_conversation: true,
    }));
    setRepairSearch(option.repair_id || option.process_id || '');
  };

  const loadRepairCandidates = async (searchTerm = '', autoFill = true) => {
    if (!conversationId) return;

    try {
      setRepairLookupLoading(true);
      setRepairLookupError('');
      const data = await getConversationRepairLinks(conversationId, searchTerm);
      const items = Array.isArray(data?.items) ? data.items : [];
      setRepairOptions(items);

      const best = items.find((item) => item.is_current)
        || data?.current
        || (items.length === 1 ? items[0] : null);

      if (autoFill && best) {
        applyRepairCandidate(best);
        setConversation((prev) => prev ? {
          ...prev,
          repair_id: best.repair_id || prev.repair_id,
          process_id: best.process_id || prev.process_id,
        } : prev);
      }
    } catch (err) {
      console.error('Error loading repair links:', err);
      setRepairOptions([]);
      setRepairLookupError(formatApiError(err, 'Repair lookup failed'));
    } finally {
      setRepairLookupLoading(false);
    }
  };

  const openConversationTriageDialog = () => {
    const triage = getConversationTriage();
    const task = triage.task || {};
    const repair = triage.repair || {};
    const initialRepairId = repair.repair_id || conversation?.repair_id || '';
    const initialProcessId = repair.process_id || conversation?.process_id || '';

    setShowConversationTriage(true);
    setRepairSearch(initialRepairId || '');
    setRepairOptions([]);
    setRepairLookupError('');
    setTriageForm({
      status: triage.status || '',
      tags: Array.isArray(triage.tags) ? triage.tags : [],
      contact_categories: Array.isArray(triage.contact_categories) ? triage.contact_categories : [],
      task_status: task.status || '',
      task_title: task.title || '',
      task_note: task.note || '',
      repair_id: initialRepairId,
      process_id: initialProcessId,
      apply_repair_to_conversation: true,
    });
    loadRepairCandidates(initialRepairId || '', true);
  };

  const toggleTriageListValue = (field, value) => {
    setTriageForm((prev) => {
      const currentValues = Array.isArray(prev[field]) ? prev[field] : [];
      return {
        ...prev,
        [field]: currentValues.includes(value)
          ? currentValues.filter((item) => item !== value)
          : [...currentValues, value],
      };
    });
  };

  const saveConversationTriage = async () => {
    if (!showConversationTriage || savingTriage) return;

    try {
      setSavingTriage(true);
      const statusValue = triageForm.status;
      const payload = {
        status: canReadTasks && ['task_open', 'task_closed'].includes(statusValue) ? undefined : statusValue,
        ...(!canReadTasks ? { task_status: triageForm.task_status || null, task_title: triageForm.task_title.trim(), task_note: triageForm.task_note.trim() } : {}),
        tags: triageForm.tags,
        contact_categories: triageForm.contact_categories,
        repair_id: triageForm.repair_id.trim(),
        process_id: triageForm.process_id.trim() || null,
        apply_repair_to_conversation: triageForm.apply_repair_to_conversation,
      };

      const updatedConversation = await updateConversationTriage(conversationId, payload);
      setConversation(updatedConversation);
      setShowConversationTriage(false);
      setError(null);
      // Refresh the inbox list so the new tags/status show in the chat overview
      // immediately — without needing a page reload.
      if (onConversationUpdated) onConversationUpdated();
    } catch (err) {
      console.error('Error updating conversation triage:', err);
      setError(formatApiError(err, 'Failed to update chat status'));
    } finally {
      setSavingTriage(false);
    }
  };

  const handleSendMessage = async (e, options = {}) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    const attachmentsForSend = options.attachments || pendingAttachments;
    const draftMessage = typeof options.message === 'string' ? options.message : newMessage;
    const channelForSend = options.channel || selectedChannel;
    if ((!draftMessage.trim() && attachmentsForSend.length === 0) || sending) return;

    const untranslated = options.sendUntranslated ? { send_untranslated: true } : {};
    try {
      setSending(true);
      setError(null);
      setTranslationOutage(null);
      const senderDisplay = user?.first_name ? `Agent (${user.first_name})` : 'Agent';
      const rawBody = draftMessage.trim();
      const commandTemplate = rawBody.startsWith('/') ? resolveTemplateCommand(rawBody) : null;
      const body = (commandTemplate ? getTemplateBody(commandTemplate) : rawBody).trim();
      if (!body && attachmentsForSend.length === 0) return;
      const selectedTemplateStillMatches = selectedTemplateMeta
        && selectedTemplateMeta.body
        && selectedTemplateMeta.body.trim() === body;
      const activeTemplate = commandTemplate || (selectedTemplateStillMatches ? selectedTemplateMeta : null);
      const activeWhatsAppTemplateName = activeTemplate?.whatsappTemplateName || null;
      const activeTemplateLanguage = activeTemplate?.language || templateLanguage;
      const activeTemplateContentSid = activeTemplate?.contentSid || null;

      if (/\{\{\s*\d+\s*\}\}/.test(body)) {
        setError('This Twilio template contains placeholders. Please replace them before sending.');
        return;
      }

      const uploadedAttachments = await uploadPendingAttachments(attachmentsForSend);
      const mediaUrl = uploadedAttachments[0]?.url || null;
      const bodyForSend = body || 'Attachment';
      let sendResult = null;

      if (conversation?.is_group) {
        // Group chat: fan the message out to every other participant on their
        // own channel. One broadcast call replaces the per-channel send.
        sendResult = await broadcastToGroup(conversationId, {
          content: bodyForSend,
          subject: conversation.subject || null,
          sender_display: senderDisplay,
        });
        const failed = sendResult?.failed || 0;
        const delivered = sendResult?.delivered || 0;
        if (failed > 0) {
          setNotice({ severity: 'warning', message: `Delivered to ${delivered}, failed for ${failed} participant(s).` });
        } else {
          setNotice(null);
        }
      } else if (channelForSend === ChannelType.SMS) {
        sendResult = await sendSms(conversationId, {
          conversation_id: conversationId,
          sender_user_id: user?.id,
          sender_display: senderDisplay,
          ...untranslated,
          body: bodyForSend,
          media_url: mediaUrl,
          attachments: uploadedAttachments,
        });
      } else if (channelForSend === ChannelType.WHATSAPP) {
        sendResult = await sendWhatsApp(conversationId, {
          conversation_id: conversationId,
          sender_user_id: user?.id,
          sender_display: senderDisplay,
          ...untranslated,
          body: bodyForSend,
          media_url: mediaUrl,
          attachments: uploadedAttachments,
          template_name: activeWhatsAppTemplateName,
          template_language: activeTemplateLanguage,
          template_content_sid: activeTemplateContentSid,
          template_category: activeTemplate?.category || null,
          template_type: activeTemplate?.contentType || null,
          template_approval_status: activeTemplate?.approvalStatus || null,
        });
      } else if (channelForSend === ChannelType.FACEBOOK) {
        sendResult = await sendFacebook(conversationId, {
          conversation_id: conversationId,
          sender_user_id: user?.id,
          sender_display: senderDisplay,
          ...untranslated,
          body: bodyForSend,
          attachments: uploadedAttachments,
        });
      } else if (channelForSend === ChannelType.INSTAGRAM) {
        sendResult = await sendInstagram(conversationId, {
          conversation_id: conversationId,
          sender_user_id: user?.id,
          sender_display: senderDisplay,
          ...untranslated,
          body: bodyForSend,
          attachments: uploadedAttachments,
        });
      } else if (channelForSend === ChannelType.EMAIL) {
        sendResult = await sendEmailFromTunnel(conversationId, {
          conversation_id: conversationId,
          sender_user_id: user?.id,
          sender_display: senderDisplay,
          ...untranslated,
          subject: 'Message from eScooterLab',
          body_html: `<p>${bodyForSend.replace(/\n/g, '<br/>')}</p>`,
          body_text: bodyForSend,
          attachments: uploadedAttachments,
        });
      } else {
        sendResult = await sendMessage(conversationId, {
          conversation_id: conversationId,
          channel: channelForSend,
          direction: MessageDirection.OUTBOUND,
          sender_display: senderDisplay,
          ...untranslated,
          sender_user_id: user?.id,
          content: bodyForSend,
          attachments: uploadedAttachments,
        });
      }

      if (conversation?.is_group) {
        // group notice already set above
      } else if (channelForSend === ChannelType.WHATSAPP && sendResult?.warning) {
        setNotice({ severity: 'warning', message: sendResult.warning });
      } else {
        setNotice(null);
      }

      if (aiDraft?.ai_draft_text) {
        submitAiResponseFeedback({
          conversation_id: conversationId,
          draft_id: aiDraft.draft_id,
          original_ai_text: aiDraft.ai_draft_text,
          final_sent_text: bodyForSend,
          // Agent's structured reason if they picked one, else auto edited/unmodified.
          feedback_category: aiFeedbackCategory
            || (bodyForSend === aiDraft.ai_draft_text ? 'ACCEPTED_UNMODIFIED' : 'AGENT_EDITED'),
          user_id: user?.id || 'agent'
        }).catch(err => console.warn('Learning feedback log error:', err));
        setAiDraft(null);
        setAiFeedbackCategory(null);
      }

      setNewMessage('');
      setSelectedTemplateMeta(null);
      attachmentsForSend.forEach((item) => {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      });
      setPendingAttachments([]);
      if (options.afterSuccess) {
        options.afterSuccess();
      }
      const nextFilter = messageChannelFilter && messageChannelFilter !== channelForSend
        ? channelForSend
        : messageChannelFilter;
      if (nextFilter !== messageChannelFilter) {
        setMessageChannelFilter(nextFilter);
      }
      shouldForceScrollRef.current = true;
      await silentRefresh(nextFilter);
      try {
        window.dispatchEvent(new CustomEvent('communication-tunnel-refresh'));
      } catch (_) { /* refresh event dispatch is best-effort */ }
      window.setTimeout(() => silentRefresh(nextFilter), 2500);
      window.setTimeout(() => silentRefresh(nextFilter), 6500);
    } catch (err) {
      console.error('Error sending message:', err);
      const outage = translationOutageOf(err);
      if (outage && !options.sendUntranslated) {
        // Keep the draft; the agent decides whether to send it untranslated.
        setTranslationOutage({ ...outage, retry: { ...options, message: draftMessage, channel: channelForSend, attachments: attachmentsForSend } });
      } else {
        setError(formatApiError(err, 'Failed to send message'));
      }
    } finally {
      setSending(false);
    }
  };

  const handleSendRecordedAudio = async (event) => {
    if (!recordedAudio?.file || sending) return;
    const audioAttachment = createAudioAttachmentItem(
      recordedAudio.file,
      recordedAudio.previewUrl,
      recordedAudio.duration
    );
    const attachments = [...pendingAttachments, audioAttachment];
    const messageText = newMessage.trim() || 'Voice message';

    await handleSendMessage(event, {
      message: messageText,
      attachments,
      afterSuccess: () => setRecordedAudio(null),
    });
  };

  const getChannelIcon = (channel, size = 20) => {
    const sx = { fontSize: size };
    switch (channel) {
      case ChannelType.EMAIL: return <EmailIcon sx={{ ...sx, color: '#1976d2' }} />;
      case ChannelType.PHONE: return <PhoneIcon sx={{ ...sx, color: '#2e7d32' }} />;
      case ChannelType.WHATSAPP: return <WhatsAppIcon sx={{ ...sx, color: '#25d366' }} />;
      case ChannelType.SMS: return <SmsIcon sx={{ ...sx, color: '#0288d1' }} />;
      case ChannelType.INSTAGRAM: return <InstagramIcon sx={{ ...sx, color: '#e1306c' }} />;
      case ChannelType.FACEBOOK: return <FacebookIcon sx={{ ...sx, color: '#1877f2' }} />;
      default: return <ChatIcon sx={{ ...sx, color: '#9e9e9e' }} />;
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    // Backend timestamps are naive UTC (datetime.utcnow(), no 'Z'). Without a
    // timezone marker the browser parses them as LOCAL time, shifting every
    // message by its UTC offset (messages look hours old). Append 'Z' so they
    // parse as UTC before we render them in Europe/Zurich.
    const s = (typeof dateStr === 'string' && !/[zZ]$|[+-]\d{2}:?\d{2}$/.test(dateStr))
      ? `${dateStr}Z` : dateStr;
    const date = new Date(s);
    return date.toLocaleString('de-CH', {
      timeZone: 'Europe/Zurich',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getInlineTranslationForMessage = (msg) => {
    const own = showOriginal ? null : ownTranslatedText(msg, translationLanguage);
    if (own) {
      return {
        translatedText: cleanDisplayText(own),
        originalText: cleanDisplayText(msg.content),
        own: true,
        customerLanguage: msg.message_metadata?.customer_language,
      };
    }
    if (!inlineTranslationActive || !msg?.id) return null;
    const item = inlineTranslationsById[msg.id];
    if (!item?.translated_text) return null;
    return {
      translatedText: cleanDisplayText(item.translated_text),
      originalText: cleanDisplayText(item.original_text),
    };
  };

  const renderOriginalTextToggle = (translation, isOutbound = false) => {
    if (!translation?.originalText || translation.originalText === translation.translatedText) return null;
    const summary = translation.own
      ? `What the customer received${translation.customerLanguage ? ` (${languageName(translation.customerLanguage)})` : ''}`
      : 'Original';

    return (
      <Box
        component="details"
        sx={{
          mt: 0.75,
          '& summary': {
            cursor: 'pointer',
            fontSize: 11,
            fontWeight: 800,
            color: isOutbound ? 'rgba(255,255,255,0.78)' : '#64748b',
          },
        }}
      >
        <summary>{summary}</summary>
        <Typography
          fontSize={12}
          sx={{
            whiteSpace: 'pre-wrap',
            mt: 0.5,
            lineHeight: 1.45,
            color: isOutbound ? 'rgba(255,255,255,0.82)' : '#64748b',
          }}
        >
          {translation.originalText}
        </Typography>
      </Box>
    );
  };

  // ─── Message renderers ───

  const renderCallMessage = (msg) => {
    const isOutbound = msg.direction === MessageDirection.OUTBOUND;
    const translation = getInlineTranslationForMessage(msg);
    const meta = msg.message_metadata || {};
    const callStatus = meta.call_status || 'completed';
    const isMissed = callStatus === 'missed' || callStatus === 'no-answer';
    const isFailed = callStatus === 'failed';
    const color = isFailed ? 'error' : isMissed ? 'warning' : 'success';
    const bgColor = isFailed ? 'rgba(211,47,47,0.06)' : isMissed ? 'rgba(237,108,2,0.06)' : 'rgba(46,125,50,0.06)';
    const borderColor = isFailed ? 'rgba(211,47,47,0.2)' : isMissed ? 'rgba(237,108,2,0.2)' : 'rgba(46,125,50,0.2)';

    return (
      <Box key={msg.id} sx={{ alignSelf: 'center', maxWidth: '65%', textAlign: 'center' }}>
        <Paper elevation={0} sx={{ bgcolor: bgColor, border: `1px solid ${borderColor}`, borderRadius: 3, px: 2.5, py: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, fontSize: 14 }}>
            {isOutbound
              ? <PhoneForwardedIcon color={color} sx={{ fontSize: 18 }} />
              : <PhoneCallbackIcon color={color} sx={{ fontSize: 18 }} />
            }
            <Typography fontSize={14} fontWeight={500}>{translation?.translatedText || msg.content}</Typography>
          </Box>
          {renderOriginalTextToggle(translation)}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {msg.sender_display} · {formatTime(msg.created_at)}
            {meta.phone_number && ` · ${meta.phone_number}`}
          </Typography>
          {(msg.has_recording || msg.has_transcript) && (
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 1 }}>
              {msg.has_recording && (
                <Button size="small" variant="outlined" startIcon={<PlayCircleIcon />}
                  sx={{ fontSize: 11, textTransform: 'none', borderRadius: 2 }}
                  onClick={() => openCallRecordingDialog(msg)}><T>
                  Recording
                </T></Button>
              )}
              {msg.has_transcript && (
                <Button size="small" variant="outlined" color="inherit"
                  startIcon={<DescriptionIcon />}
                  sx={{ fontSize: 11, textTransform: 'none', borderRadius: 2 }}
                  onClick={() => openTranscriptDialog(msg)}><T>
                  Transcript
                </T></Button>
              )}
            </Box>
          )}
        </Paper>
      </Box>
    );
  };

  const renderNoteMessage = (msg) => {
    const isSystem = msg.channel === ChannelType.SYSTEM;
    const translation = getInlineTranslationForMessage(msg);
    const meta = msg.message_metadata || {};
    const stepName = meta.step_name;
    return (
      <Box key={msg.id} sx={{ alignSelf: 'center', maxWidth: '75%' }}>
        <Paper elevation={0} sx={{
          bgcolor: isSystem ? 'rgba(100,116,139,0.06)' : 'rgba(251,191,36,0.08)',
          border: `1px solid ${isSystem ? 'rgba(100,116,139,0.18)' : 'rgba(251,191,36,0.25)'}`,
          borderRadius: 3, px: 2.5, py: 1.25,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography fontSize={12} fontWeight={700} sx={{ color: isSystem ? '#475569' : '#92400e' }}>
              {isSystem ? 'System' : msg.sender_display}
            </Typography>
            {stepName && (
              <Chip label={stepName} size="small" sx={{ fontSize: 10, height: 18, bgcolor: '#f1f5f9', color: '#64748b' }} />
            )}
            <Typography variant="caption" sx={{ color: '#94a3b8', ml: 'auto', fontSize: 10 }}>
              {formatTime(msg.created_at)}
            </Typography>
          </Box>
          <Typography fontSize={13} sx={{ color: '#334155', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
            {translation?.translatedText || msg.content}
          </Typography>
          {renderOriginalTextToggle(translation)}
        </Paper>
      </Box>
    );
  };

  const renderMessageAttachments = (msg, isOutbound = false) => {
    const attachments = msg?.attachments || [];
    if (!attachments || attachments.length === 0) return null;

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.85 }}>
        {attachments.map((att, idx) => {
          const url = resolveCommunicationAttachmentUrl(att.url, msg?.id, idx);
          const filename = att.filename || `Attachment ${idx + 1}`;
          const Icon = attachmentIcon(att.content_type || '');
          const isStoredMedia = Boolean(att.attachment_id || /\/conversations\/(attachments\/|messages\/[^/]+\/attachments\/)/.test(url));

          if (isImageAttachment(att)) {
            // Other channel providers may still own their media URLs. Preserve
            // those previews until that channel has a stored tunnel attachment.
            const isStoredImage = att.attachment_id || /\/conversations\/(attachments\/|messages\/[^/]+\/attachments\/)/.test(url);
            if (!isStoredImage) {
              return (
                <Box key={`${filename}-${idx}`} component="a" href={url} target="_blank" rel="noopener noreferrer" sx={{ display: 'block', width: 180, maxWidth: '100%', borderRadius: 2.5, overflow: 'hidden' }}>
                  <Box component="img" src={url} alt={filename} sx={{ display: 'block', width: '100%', maxHeight: 180, objectFit: 'cover' }} />
                </Box>
              );
            }
            return (
              <ChatImageAttachment
                key={`${msg.id}-${idx}`}
                attachment={att}
                messageId={msg.id}
                attachmentIndex={idx}
                isOutbound={isOutbound}
              />
            );
          }

          if (isVideoAttachment(att)) {
            return (
              <Box
                key={`${filename}-${idx}`}
                sx={{
                  width: 220,
                  maxWidth: '100%',
                  borderRadius: 2.5,
                  overflow: 'hidden',
                  border: `1px solid ${isOutbound ? 'rgba(255,255,255,0.28)' : '#e2e8f0'}`,
                  bgcolor: isOutbound ? 'rgba(255,255,255,0.12)' : '#f8fafc',
                }}
              >
                <ChatMediaAttachment
                  mediaType="video"
                  messageId={msg.id}
                  attachmentIndex={idx}
                  filename={filename}
                  sourceUrl={url}
                  isStored={isStoredMedia}
                  sx={{ display: 'block', width: '100%', maxHeight: 220, bgcolor: '#0f172a' }}
                />
              </Box>
            );
          }

          if (isAudioAttachment(att)) {
            const attachmentId = att.attachment_id;
            const transcript = getAttachmentTranscript(msg, attachmentId);
            const transcriptText = (transcript?.transcript || '').trim();
            const attachmentKey = `${msg?.id || 'message'}-${attachmentId || idx}`;
            const isTranscribingThis = transcribingAttachmentKey === attachmentKey;
            return (
              <Box
                key={`${filename}-${idx}`}
                sx={{
                  width: 300,
                  maxWidth: '100%',
                  borderRadius: 2.5,
                  p: 1,
                  border: `1px solid ${isOutbound ? 'rgba(255,255,255,0.28)' : '#e2e8f0'}`,
                  bgcolor: isOutbound ? 'rgba(255,255,255,0.14)' : '#f8fafc',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                  <VoiceNoteIcon sx={{ fontSize: 16, color: isOutbound ? 'white' : '#166534' }} />
                  <Typography
                    fontSize={12}
                    fontWeight={700}
                    sx={{
                      color: isOutbound ? 'white' : '#0f172a',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {filename}
                  </Typography>
                  {attachmentId && (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={isTranscribingThis ? <CircularProgress size={12} color="inherit" /> : <DescriptionIcon sx={{ fontSize: 14 }} />}
                      onClick={() => handleTranscribeAttachment(msg, att)}
                      disabled={Boolean(transcribingAttachmentKey)}
                      sx={{
                        ml: 'auto',
                        minWidth: 0,
                        px: 1,
                        py: 0.25,
                        borderRadius: 2,
                        fontSize: 11,
                        fontWeight: 800,
                        bgcolor: isOutbound ? 'rgba(255,255,255,0.22)' : '#16a34a',
                        color: 'white',
                        boxShadow: 'none',
                        '&:hover': {
                          bgcolor: isOutbound ? 'rgba(255,255,255,0.3)' : '#15803d',
                          boxShadow: 'none',
                        },
                      }}
                    >
                      {isTranscribingThis ? 'Text...' : 'Text'}
                    </Button>
                  )}
                </Box>
                <ChatMediaAttachment
                  mediaType="audio"
                  messageId={msg.id}
                  attachmentIndex={idx}
                  filename={filename}
                  sourceUrl={url}
                  isStored={isStoredMedia}
                  sx={{ display: 'block', width: '100%', height: 34 }}
                />
                {transcriptText && (
                  <Box
                    sx={{
                      mt: 0.85,
                      p: 1,
                      borderRadius: 2,
                      bgcolor: isOutbound ? 'rgba(255,255,255,0.12)' : '#ecfdf5',
                      border: `1px solid ${isOutbound ? 'rgba(255,255,255,0.18)' : '#bbf7d0'}`,
                    }}
                  >
                    <Typography
                      fontSize={11}
                      fontWeight={800}
                      sx={{ mb: 0.4, color: isOutbound ? 'rgba(255,255,255,0.75)' : '#15803d' }}
                    ><T>
                      Transcript</T>{transcript?.language ? ` · ${transcript.language}` : ''}
                    </Typography>
                    <Typography
                      fontSize={12}
                      sx={{
                        color: isOutbound ? 'white' : '#0f172a',
                        lineHeight: 1.45,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {transcriptText}
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          }

          if (isStoredMedia) return (
            <ChatFileAttachment
              key={msg.id + '-' + idx}
              messageId={msg.id}
              attachmentIndex={idx}
              filename={filename}
              label={filename + (att.size ? ' · ' + formatFileSize(att.size) : '')}
              icon={<Icon sx={{ fontSize: 14 }} />}
              sx={{
                fontSize: 12, height: 26, borderRadius: 3, maxWidth: 260,
                bgcolor: isOutbound ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.04)',
                color: isOutbound ? 'white' : 'inherit',
              }}
            />
          );
          return (
            <Chip
              key={`${filename}-${idx}`}
              component="a"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              clickable
              icon={<Icon sx={{ fontSize: 14 }} />}
              label={`${filename}${att.size ? ` · ${formatFileSize(att.size)}` : ''}`}
              size="small"
              sx={{
                fontSize: 12, height: 26, borderRadius: 3,
                maxWidth: 260,
                bgcolor: isOutbound ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.04)',
                color: isOutbound ? 'white' : 'inherit',
              }}
            />
          );
        })}
      </Box>
    );
  };

  const getMessageReactions = (msg) => (
    Array.isArray(msg?.message_metadata?.reactions)
      ? msg.message_metadata.reactions.filter((reaction) => reaction?.emoji)
      : []
  );

  const renderMessageReactions = (msg, isOutbound) => {
    const reactions = getMessageReactions(msg);
    if (!reactions.length) return null;

    const grouped = reactions.reduce((acc, reaction) => {
      const emoji = reaction.emoji;
      if (!acc[emoji]) acc[emoji] = { emoji, count: 0, names: [] };
      acc[emoji].count += 1;
      if (reaction.actor_display) acc[emoji].names.push(reaction.actor_display);
      return acc;
    }, {});

    return (
      <Box sx={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: isOutbound ? 'flex-end' : 'flex-start',
        gap: 0.35,
        mt: 0.75,
      }}>
        {Object.values(grouped).map((reaction) => (
          <Tooltip
            key={reaction.emoji}
            title={reaction.names.length ? reaction.names.join(', ') : 'Reaction'}
            arrow
          >
            <Box component="span" sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.35,
              px: 0.75,
              height: 24,
              borderRadius: 999,
              bgcolor: isOutbound ? 'rgba(255,255,255,0.92)' : '#f8fafc',
              color: '#0f172a',
              border: '1px solid rgba(148,163,184,0.35)',
              boxShadow: '0 2px 7px rgba(15,23,42,0.10)',
              fontSize: 15,
              fontWeight: 800,
              lineHeight: 1,
            }}>
              <Box component="span" role="img" aria-label={`Reaction ${reaction.emoji}`}>
                {reaction.emoji}
              </Box>
              {reaction.count > 1 && (
                <Typography component="span" fontSize={11} fontWeight={900} color="#475569">
                  {reaction.count}
                </Typography>
              )}
            </Box>
          </Tooltip>
        ))}
      </Box>
    );
  };

  const getStatusStyle = (status) => {
    const normalized = (status || '').toLowerCase();
    if (['answered', 'task_closed'].includes(normalized)) {
      return { bg: '#dcfce7', text: '#166534', border: '#86efac' };
    }
    if (['needs_read', 'task_open', 'undone'].includes(normalized)) {
      return { bg: '#fff7ed', text: '#c2410c', border: '#fed7aa' };
    }
    if (normalized === 'important') {
      return { bg: '#fef2f2', text: '#b91c1c', border: '#fecaca' };
    }
    if (['spam', 'blocked', 'cancelled', 'canceled'].includes(normalized)) {
      return { bg: '#fee2e2', text: '#991b1b', border: '#fecaca' };
    }
    return { bg: '#f1f5f9', text: '#475569', border: '#cbd5e1' };
  };

  const renderConversationTriageChips = () => {
    const triage = getConversationTriage();
    const tags = Array.isArray(triage.tags) ? triage.tags : [];
    const categories = Array.isArray(triage.contact_categories) ? triage.contact_categories : [];
    const taskStatus = triage.task?.status;
    const repairId = triage.repair?.repair_id;
    const statusStyle = getStatusStyle(triage.status);
    const chips = [
      triage.status && { label: statusLabel(triage.status), color: statusStyle.bg, text: statusStyle.text, border: statusStyle.border },
      ...tags.map((tag) => ({ label: tag, color: '#dcfce7', text: '#166534' })),
      ...categories.map((category) => ({ label: category, color: '#fef3c7', text: '#92400e' })),
      taskStatus && { label: `task ${taskStatus}`, color: taskStatus === 'closed' ? '#e5e7eb' : '#ede9fe', text: taskStatus === 'closed' ? '#374151' : '#5b21b6' },
      repairId && { label: repairId, color: '#eef2ff', text: '#3730a3' },
    ].filter(Boolean);

    if (!chips.length) return null;

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, minWidth: 0 }}>
        {chips.map((chip, index) => (
          <Chip
            key={`${chip.label}-${index}`}
            label={chip.label}
            size="small"
            sx={{
              height: 19,
              fontSize: 10,
              fontWeight: 800,
              bgcolor: chip.color,
              color: chip.text,
              border: `1px solid ${chip.border || 'transparent'}`,
              borderRadius: 3,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        ))}
      </Box>
    );
  };

  const renderMessage = (msg) => {
    const isOutbound = msg.direction === MessageDirection.OUTBOUND;
    const isPhone = msg.channel === ChannelType.PHONE;
    const isNote = msg.channel === ChannelType.NOTE;
    const isSystemMsg = msg.channel === ChannelType.SYSTEM;
    const translation = getInlineTranslationForMessage(msg);

    if (isPhone) return renderCallMessage(msg);
    if (isNote || isSystemMsg) return renderNoteMessage(msg);

    // Channel-specific outbound gradient
    const channelGradients = {
      [ChannelType.SMS]: 'linear-gradient(135deg, #0288d1 0%, #039be5 100%)',
      [ChannelType.FACEBOOK]: 'linear-gradient(135deg, #1877f2 0%, #1565c0 100%)',
      [ChannelType.INSTAGRAM]: 'linear-gradient(135deg, #f77737 0%, #e1306c 50%, #c13584 100%)',
      [ChannelType.WHATSAPP]: 'linear-gradient(135deg, #128c7e 0%, #25d366 100%)',
    };
    const defaultGradient = `linear-gradient(135deg, #1a8a34 0%, #2bb535 50%, ${GREEN} 100%)`;
    const outboundBg = channelGradients[msg.channel] || defaultGradient;

    const channelBorders = {
      [ChannelType.SMS]: '#0288d1',
      [ChannelType.FACEBOOK]: '#1877f2',
      [ChannelType.INSTAGRAM]: '#e1306c',
      [ChannelType.WHATSAPP]: '#25d366',
    };

    return (
      <Box
        key={msg.id}
        className="message-row"
        sx={{ alignSelf: isOutbound ? 'flex-end' : 'flex-start', maxWidth: '70%' }}
      >
        <Paper
          elevation={0}
          sx={{
            px: 1.75, py: 1.25, borderRadius: '18px',
            position: 'relative',
            ...(isOutbound ? {
              background: outboundBg,
              color: 'white',
              borderBottomRightRadius: '4px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            } : {
              bgcolor: 'white',
              borderBottomLeftRadius: '4px',
              border: '1px solid #e0e0e0',
              borderLeftColor: channelBorders[msg.channel] || '#e0e0e0',
              borderLeftWidth: channelBorders[msg.channel] ? 3 : 1,
            }),
          }}
        >
          <Tooltip title={at("React")} arrow>
            <IconButton
              size="small"
              onClick={(event) => openReactionMenu(event, msg)}
              sx={{
                position: 'absolute',
                top: -12,
                ...(isOutbound ? { left: -12 } : { right: -12 }),
                width: 28,
                height: 28,
                bgcolor: 'white',
                color: '#475569',
                border: '1px solid #e2e8f0',
                boxShadow: '0 3px 10px rgba(15,23,42,0.16)',
                opacity: 0.78,
                zIndex: 1,
                '&:hover': { bgcolor: '#f8fafc', color: '#16a34a', opacity: 1 },
              }}
            >
              <AddReactionIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {getChannelIcon(msg.channel, 14)}
              <Typography fontSize={11} fontWeight={600} sx={{ color: isOutbound ? 'rgba(255,255,255,0.85)' : '#8a8d91' }}>
                {isOutbound ? msg.sender_display : 'Customer'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {canReadTasks && (
                <Tooltip title={at("Tasks for this message")} arrow>
                  <IconButton
                    size="small"
                    aria-label={at("Tasks for this message")}
                    onClick={() => setTaskSource({ kind: 'message', source_id: msg.id, conversation_id: conversationId })}
                    sx={{ p: 0.25, color: isOutbound ? 'rgba(255,255,255,0.8)' : '#94a3b8', '&:hover': { color: isOutbound ? '#fff' : '#166534' } }}
                  >
                    <TaskAltIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
              <AccessTimeIcon sx={{ fontSize: 11, opacity: 0.7 }} />
              <Typography fontSize={10} sx={{ color: isOutbound ? 'rgba(255,255,255,0.7)' : '#8a8d91' }}>
                {formatTime(msg.created_at)}
              </Typography>
            </Box>
          </Box>

          {/* Subject */}
          {!translation?.translatedText && msg.subject && (
            <Typography fontSize={13} fontWeight={700} sx={{
              pb: 0.5, mb: 0.5, borderBottom: '1px solid',
              borderColor: isOutbound ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.06)',
            }}>
              {msg.subject}
            </Typography>
          )}

          {/* Content */}
          <Box sx={{ fontSize: 14, lineHeight: 1.45, whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
            {translation?.translatedText ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75, flexWrap: 'wrap' }}>
                  <Chip
                    label={translation.own
                      ? `Sent in ${languageName(translation.customerLanguage)} · your text`
                      : `Translated ${inlineTranslation?.languageLabel || templateLanguageLabel}`}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: 10,
                      fontWeight: 900,
                      bgcolor: isOutbound ? 'rgba(255,255,255,0.2)' : '#e0f2fe',
                      color: isOutbound ? 'white' : '#075985',
                      '& .MuiChip-label': { px: 0.75 },
                    }}
                  />
                  {msg.content_html && (
                    <Button
                      size="small"
                      variant={isOutbound ? 'contained' : 'outlined'}
                      startIcon={<EmailIcon sx={{ fontSize: 13 }} />}
                      onClick={() => openEmailModal(msg)}
                      sx={{
                        fontSize: 11, textTransform: 'none', borderRadius: 2, py: 0, px: 1, minHeight: 24,
                        ...(isOutbound ? {
                          bgcolor: 'rgba(255,255,255,0.2)', color: 'white', boxShadow: 'none',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                        } : {}),
                      }}
                    ><T>
                      View Email
                    </T></Button>
                  )}
                </Box>
                <Typography fontSize={14} sx={{ lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>
                  {translation.translatedText}
                </Typography>
                {renderOriginalTextToggle(translation, isOutbound)}
              </>
            ) : msg.content_html ? (
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Typography component="span" fontSize={13} sx={{ color: isOutbound ? 'rgba(255,255,255,0.9)' : '#555' }}>
                  {(() => {
                    const isSys = msg.message_metadata?.source === 'notification_service';
                    const eventType = msg.message_metadata?.event_type;
                    if (isSys && eventType && EVENT_TYPE_LABELS[eventType]) return EVENT_TYPE_LABELS[eventType];
                    const plain = stripHtml(msg.content_html);
                    return plain.substring(0, 120) + (plain.length > 120 ? '...' : '') || 'Email content';
                  })()}
                </Typography>
                <Button
                  size="small"
                  variant={isOutbound ? 'contained' : 'outlined'}
                  startIcon={<EmailIcon sx={{ fontSize: 13 }} />}
                  onClick={() => openEmailModal(msg)}
                  sx={{
                    fontSize: 11, textTransform: 'none', borderRadius: 2, py: 0, px: 1, minHeight: 24,
                    ...(isOutbound ? {
                      bgcolor: 'rgba(255,255,255,0.2)', color: 'white', boxShadow: 'none',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                    } : {}),
                  }}
                ><T>
                  View Email
                </T></Button>
              </Box>
            ) : templatePlaceholderName(msg.content) ? (
              <>
                <Chip
                  label={`WhatsApp template · ${templatePlaceholderName(msg.content)}`}
                  size="small"
                  sx={{ height: 20, fontSize: 10, fontWeight: 900, mb: 0.75, bgcolor: isOutbound ? 'rgba(255,255,255,0.2)' : '#e0f2fe', color: isOutbound ? 'white' : '#075985' }}
                />
                <Typography fontSize={14} sx={{ lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>
                  {templateTextByName[templatePlaceholderName(msg.content)]
                    || (twilioTemplatesLoaded ? 'Approved template sent. Its text is no longer available in Twilio.' : 'Loading template text…')}
                </Typography>
              </>
            ) : (
              <>
                {msg.message_metadata?.twilio_template_name && msg.message_metadata?.twilio_send_mode === 'content_template' && (
                  <Chip
                    label={`WhatsApp template · ${msg.message_metadata.twilio_template_name}`}
                    size="small"
                    sx={{ height: 20, fontSize: 10, fontWeight: 900, mb: 0.75, bgcolor: isOutbound ? 'rgba(255,255,255,0.2)' : '#e0f2fe', color: isOutbound ? 'white' : '#075985' }}
                  />
                )}
                {msg.message_metadata?.bot_template_fallback && (
                  <Tooltip
                    arrow
                    title={`The bot answered with its normal text because ${msg.message_metadata.twilio_template_requested_name || 'the template for this step'} ${msg.message_metadata.bot_template_fallback === 'template_rejected' ? 'was rejected by Twilio' : 'is not approved'}. Approve it in Twilio.`}
                  >
                    <Chip
                      label={at("Bot text · template not approved")}
                      size="small"
                      sx={{ height: 20, fontSize: 10, fontWeight: 900, mb: 0.75, bgcolor: '#fef3c7', color: '#92400e' }}
                    />
                  </Tooltip>
                )}
                <Typography fontSize={14} sx={{ lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>
                  {msg.content}
                </Typography>
              </>
            )}
          </Box>

          {isOutbound && msg.message_metadata?.translation_skipped && (
            <Typography sx={{ mt: 0.5, fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.85)' }}>
              {`Sent untranslated in ${languageName(msg.message_metadata.delivered_language)} (translation was unavailable)`}
            </Typography>
          )}
          {renderMessageAttachments(msg, isOutbound)}
          {renderMessageReactions(msg, isOutbound)}

          {isOutbound && ['failed', 'undelivered'].includes(msg.delivery_status) && (
            <Box
              role="note"
              sx={{
                display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: 0.75, px: 1, py: 0.6,
                borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.94)', color: '#991b1b',
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 15, mt: '1px', flexShrink: 0 }} />
              <Typography sx={{ fontSize: 12, lineHeight: 1.35, fontWeight: 600 }}>
                {msg.delivery_status === 'failed' ? 'Not sent: ' : 'Not delivered: '}
                {deliveryFailureText(msg.message_metadata)}
                {msg.message_metadata?.twilio_error_code ? ` (Code ${msg.message_metadata.twilio_error_code})` : ''}
              </Typography>
            </Box>
          )}

          {/* Footer / delivery status */}
          {isOutbound && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, mt: 0.25, flexWrap: 'wrap' }}>
              {msg.message_metadata?.recipient_to && (
                <Typography fontSize={10} sx={{ color: 'rgba(255,255,255,0.45)', fontFamily: 'monospace' }}>
                  {msg.message_metadata.recipient_to}
                </Typography>
              )}
              {(() => {
                // Keep a visible baseline state even before Twilio sends the
                // first callback. This prevents an outbound message from
                // appearing to have no status at all.
                const st = msg.delivery_status || 'sent';
                const errCode = msg.message_metadata?.twilio_error_code;
                const errLabel = errCode ? (TWILIO_ERROR_REASONS[String(errCode)] || `Err ${errCode}`) : null;
                const errMessage = msg.message_metadata?.twilio_error_message;
                const tooltipMap = {
                  delivered: msg.channel === 'EMAIL' ? 'Delivered to the receiving mail server' : '✅ 100% delivered to recipient\'s phone',
                  read:      '✅ 100% delivered and read by recipient',
                  sent:      msg.channel === 'EMAIL' ? 'Accepted by the mail server for delivery' : '⚠️ Reached WhatsApp servers — waiting for delivery confirmation to phone',
                  failed:    '❌ Message failed to send',
                  undelivered: errLabel === 'Blocked'
                    ? '🚫 Recipient has blocked this WhatsApp number'
                    : `❌ Not delivered${errLabel ? ` — ${errLabel}` : ''}`,
                  queued:    '⏳ Message queued, not yet sent',
                  accepted:  '⏳ Accepted by Twilio, not yet sent',
                };
                const label = st === 'delivered' ? '✓✓ Delivered'
                  : st === 'read'        ? '✓✓ Read'
                  : st === 'sent'        ? '✓ Sent'
                  : st === 'failed'      ? '✗ Failed'
                  : st === 'undelivered' ? `! Undelivered${errLabel ? ` (${errLabel})` : ''}`
                  : st === 'queued'      ? '⏳ Sending…'
                  : st === 'accepted'    ? '⏳ Accepted'
                  : st;
                const color = st === 'delivered' ? '#a5d6a7'
                  : st === 'read'        ? '#90caf9'
                  : st === 'failed'      ? '#ef9a9a'
                  : st === 'undelivered' ? '#ffe082'
                  : st === 'sent'        ? '#ffe082'
                  : (st === 'queued' || st === 'accepted') ? '#fff9c4'
                  : 'rgba(255,255,255,0.6)';
                return (
                  <Tooltip title={[tooltipMap[st] || st, errMessage].filter(Boolean).join(' — ')} placement="top" arrow>
                    <Typography fontSize={11} sx={{ color, cursor: 'default' }}>
                      {label}
                    </Typography>
                  </Tooltip>
                );
              })()}
            </Box>
          )}
        </Paper>
      </Box>
    );
  };

  // ─── Loading / empty states ───
  const shellSx = {
    borderRadius: embedded ? 2 : (mobileFullScreen ? 0 : 3),
    border: mobileFullScreen ? 0 : '1px solid',
    borderColor: 'divider',
    display: 'flex',
    flexDirection: 'column',
    // embedded: fill whatever height the parent gives us (call popup panel)
    height: embedded ? '100%' : (mobileFullScreen ? '100dvh' : 'calc(100dvh - 175px)'),
    minHeight: embedded ? 0 : (mobileFullScreen ? 0 : 560),
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
  };

  if (loading) {
    return (
      <Paper elevation={0} sx={{
        ...shellSx,
        alignItems: 'center', justifyContent: 'center',
      }}>
        <CircularProgress sx={{ color: GREEN }} />
      </Paper>
    );
  }

  if (!conversation) {
    return (
      <Paper elevation={0} sx={{
        ...shellSx,
        alignItems: 'center', justifyContent: 'center',
      }}>
        <ChatIcon sx={{ fontSize: 48, color: '#d1d5db', mb: 1 }} />
        <Typography color="text.secondary"><T>Select a conversation to view</T></Typography>
      </Paper>
    );
  }

  const customerId = conversation.customer_id || conversation.actor_id || null;
  const customerLanguageHint = !conversation.is_group && conversation.customer_language
    && ![ChannelType.NOTE, ChannelType.PHONE].includes(selectedChannel)
    ? {
      language: conversation.customer_language,
      source: conversation.customer_language_source,
      translates: conversation.customer_language !== translationLanguage,
    }
    : null;
  const openClient = () => {
    if (customerId) {
      setActorDialogOpen(true);
    }
  };
  const handleActorSaved = (savedActor) => {
    setCustomerBarKey((key) => key + 1);
    if (!savedActor?.id) return;
    const savedName = savedActor.type === 'enterprise'
      ? savedActor.name
      : `${savedActor.first_name || ''} ${savedActor.last_name || ''}`.trim();
    if (!savedName) return;
    setConversation((current) => {
      if (!current) return current;
      const currentName = current.customer_display_name || '';
      if (/^Client\s/i.test(currentName)) return current;
      return {
        ...current,
        customer_display_name: current.alias_code ? `${savedName} (${current.alias_code})` : savedName,
      };
    });
  };
  const openRepair = () => {
    // Stamp the current history entry with this conversation so pressing browser
    // Back from the repair returns straight to this chat (the hub reopens it
    // from the ?conversation= param).
    try {
      const convId = conversation.conversation_id || conversation.id;
      if (convId) {
        window.history.replaceState(null, '', `${window.location.pathname}?conversation=${convId}`);
      }
    } catch (_) { /* noop */ }
    if (conversation.process_id) {
      navigate(`/repair-processes/${conversation.process_id}`);
    } else if (conversation.repair_id) {
      navigate(`/repair-processes?search=${encodeURIComponent(conversation.repair_id)}`);
    }
  };
  const conversationTriage = getConversationTriage();
  const conversationStatusStyle = getStatusStyle(conversationTriage.status);
  const conversationTags = Array.isArray(conversationTriage.tags) ? conversationTriage.tags : [];
  const conversationTask = conversationTriage.task || {};
  const taskButtonLabel = conversationTask.status
    ? `Task: ${conversationTask.status === 'closed' ? 'Closed' : 'Open'}`
    : 'Task';

  return (
    <Paper elevation={0} sx={shellSx}>
      {/* ─── Header ─── */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        px: mobileFullScreen ? 1 : 2,
        py: mobileFullScreen ? 1 : 1.25,
        borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'white', flexShrink: 0,
        minHeight: mobileFullScreen ? 58 : 'auto',
      }}>
        <IconButton onClick={onBack} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            component={customerId ? 'button' : 'div'}
            type={customerId ? 'button' : undefined}
            onClick={customerId ? openClient : undefined}
            title={customerId ? 'Actor information' : undefined}
            fontWeight={700}
            fontSize={15}
            noWrap
            sx={{
              display: 'block',
              maxWidth: '100%',
              border: 0,
              bgcolor: 'transparent',
              p: 0,
              color: 'text.primary',
              textAlign: 'left',
              cursor: customerId ? 'pointer' : 'default',
              '&:hover': customerId ? { color: GREEN, textDecoration: 'underline' } : undefined,
            }}
          >
            {conversation.is_group
              ? (conversation.subject || 'Group chat')
              : (conversation.customer_display_name || 'Dear Client')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary">
              {conversation.conversation_number}
            </Typography>
            {Array.isArray(conversation.channels) && conversation.channels.map((ch, idx) => {
              const upper = String(ch).toUpperCase();
              const iconSx = { fontSize: 12, mr: -0.5 };
              const meta = upper === 'WHATSAPP' ? { label: 'WhatsApp', icon: <WhatsAppIcon sx={{ ...iconSx, color: '#25d366' }} /> }
                : upper === 'FACEBOOK' ? { label: 'Facebook', icon: <FacebookIcon sx={{ ...iconSx, color: '#1877f2' }} /> }
                : upper === 'INSTAGRAM' ? { label: 'Instagram', icon: <InstagramIcon sx={{ ...iconSx, color: '#e1306c' }} /> }
                : upper === 'EMAIL' ? { label: 'Email', icon: <EmailIcon sx={{ ...iconSx, color: '#2563eb' }} /> }
                : upper === 'PHONE' ? { label: 'Phone', icon: <PhoneIcon sx={{ ...iconSx, color: '#16a34a' }} /> }
                : upper === 'SMS' ? { label: 'SMS', icon: <SmsIcon sx={{ ...iconSx, color: '#0ea5e9' }} /> }
                : { label: upper, icon: null };
              return (
                <Chip
                  key={idx}
                  icon={meta.icon}
                  label={meta.label}
                  size="small"
                  sx={{ fontSize: 10, height: 18, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', fontWeight: 600, px: 0.5 }}
                />
              );
            })}
            {conversation.is_group && (
              <Chip
                label={`👥 Group${groupParticipants.length ? ` · ${groupParticipants.length}` : ''}`}
                size="small"
                sx={{
                  fontSize: 11,
                  height: 20,
                  bgcolor: '#eef2ff',
                  color: '#4338ca',
                  fontWeight: 700,
                  border: '1px solid #c7d2fe',
                }}
              />
            )}
            {/* Repair tracking id — clickable, opens the repair; Back returns here.
                (The client code already appears in the title above, so the old
                green "Client CLT-…" chip was removed to stop the duplicate.) */}
            {conversation.repair_id && (
              <Chip
                label={`🔧 ${conversation.repair_id} ↗`}
                size="small"
                clickable
                onClick={openRepair}
                title={at("Open repair process — Back returns to this chat")}
                sx={{
                  fontSize: 11,
                  height: 20,
                  bgcolor: '#eff6ff',
                  color: '#1d4ed8',
                  fontWeight: 800,
                  border: '1px solid #bfdbfe',
                  '&:hover': { bgcolor: '#dbeafe' },
                }}
              />
            )}
          </Box>
          {conversation.is_group && groupParticipants.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.6 }}>
              {groupParticipants.map((p) => {
                const ch = (p.channel || '').toUpperCase();
                const chMeta = ch === 'WHATSAPP' ? { t: 'WhatsApp', c: '#16a34a', bg: '#f0fdf4' }
                  : ch === 'SMS' ? { t: 'SMS', c: '#0369a1', bg: '#f0f9ff' }
                  : ch === 'EMAIL' ? { t: 'Email', c: '#1d4ed8', bg: '#eff6ff' }
                  : { t: 'ERP', c: '#6b7280', bg: '#f1f5f9' };
                const role = (p.role || 'CUSTOMER').toUpperCase();
                const roleLabel = role.charAt(0) + role.slice(1).toLowerCase();
                return (
                  <Box
                    key={p.id}
                    sx={{
                      display: 'inline-flex', alignItems: 'center', gap: 0.6,
                      pl: 0.5, pr: 0.85, py: '2px', borderRadius: 1.5,
                      bgcolor: '#fff', border: '1px solid #e2e8f0',
                    }}
                  >
                    <Box component="span" sx={{ fontSize: 9, fontWeight: 800, color: chMeta.c, bgcolor: chMeta.bg, px: 0.5, py: '1px', borderRadius: 0.75, letterSpacing: 0.2 }}>
                      {chMeta.t}
                    </Box>
                    <Typography component="span" sx={{ fontSize: 11.5, fontWeight: 700, color: '#0f172a' }}>
                      {p.display_name}
                    </Typography>
                    {role !== 'CUSTOMER' && (
                      <Typography component="span" sx={{ fontSize: 10, color: '#64748b' }}>· {roleLabel}</Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
        {!emailOnly && canDial && (
        <Button
          variant="contained"
          startIcon={<PhoneIcon />}
          onClick={() => setShowCallModal(true)}
          disabled={sending}
          aria-label={at("Call customer")}
          sx={{
            borderRadius: 2.5,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 14,
            px: mobileFullScreen ? 1.15 : 2.5,
            minWidth: mobileFullScreen ? 42 : 132,
            background: `linear-gradient(135deg, ${GREEN} 0%, #28a428 100%)`,
            boxShadow: '0 2px 8px rgba(50,205,50,0.25)',
            '&:hover': { background: `linear-gradient(135deg, #28a428 0%, #1a7a34 100%)` },
            '& .MuiButton-startIcon': { mr: mobileFullScreen ? 0 : 1 },
          }}
        >
          {mobileFullScreen ? '' : 'Call Customer'}
        </Button>
        )}
        <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} size="small">
          <MoreVertIcon />
        </IconButton>
        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}
          PaperProps={{ sx: { borderRadius: 2, minWidth: 180 } }}
        >
          <MenuItem onClick={() => {
            setMenuAnchor(null);
            openConversationTriageDialog();
          }}>
            <ListItemIcon><DescriptionIcon fontSize="small" sx={{ color: '#166534' }} /></ListItemIcon>
            <ListItemText><T>Status / Tags / Task</T></ListItemText>
          </MenuItem>
          <MenuItem onClick={() => {
            setMenuAnchor(null);
            handleOpenLearningRecs();
          }}>
            <ListItemIcon><AutoFixHighIcon fontSize="small" sx={{ color: '#16a34a' }} /></ListItemIcon>
            <ListItemText><T>🧠 AI Learning Recs</T></ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem disabled={markingRead} onClick={handleManualMarkAsRead}>
            <ListItemIcon><MarkChatReadIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{markingRead ? 'Marking...' : 'Mark as read'}</ListItemText>
          </MenuItem>
          <MenuItem
            disabled={Boolean(quickStatusUpdating)}
            onClick={() => handleQuickConversationStatus('important')}
          >
            <ListItemIcon><FlagIcon fontSize="small" sx={{ color: '#dc2626' }} /></ListItemIcon>
            <ListItemText>{quickStatusUpdating === 'important' ? 'Saving...' : 'Mark as important'}</ListItemText>
          </MenuItem>
          <MenuItem
            disabled={Boolean(quickStatusUpdating)}
            onClick={() => handleQuickConversationStatus('undone')}
          >
            <ListItemIcon><ChatIcon fontSize="small" sx={{ color: '#c2410c' }} /></ListItemIcon>
            <ListItemText>{quickStatusUpdating === 'undone' ? 'Saving...' : 'Mark as undone'}</ListItemText>
          </MenuItem>
          <MenuItem
            disabled={Boolean(quickStatusUpdating)}
            onClick={() => handleQuickConversationStatus('answered')}
          >
            <ListItemIcon><DoneAllIcon fontSize="small" sx={{ color: '#16a34a' }} /></ListItemIcon>
            <ListItemText>{quickStatusUpdating === 'answered' ? 'Saving...' : 'Mark as done'}</ListItemText>
          </MenuItem>
          <MenuItem
            disabled={Boolean(quickStatusUpdating)}
            onClick={() => handleQuickConversationStatus('ignored')}
          >
            <ListItemIcon><VisibilityOffIcon fontSize="small" sx={{ color: '#64748b' }} /></ListItemIcon>
            <ListItemText>{quickStatusUpdating === 'ignored' ? 'Saving...' : 'Mark as ignored'}</ListItemText>
          </MenuItem>
          <MenuItem
            disabled={Boolean(quickStatusUpdating)}
            onClick={() => handleQuickConversationStatus('cancelled')}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon><CancelIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>{quickStatusUpdating === 'cancelled' ? 'Saving...' : 'Mark as cancelled'}</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem
            disabled={closingConversation}
            onClick={requestCloseConversation}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon><CloseIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText><T>Close conversation</T></ListItemText>
          </MenuItem>
        </Menu>
        <Menu
          anchorEl={reactionAnchor}
          open={Boolean(reactionAnchor)}
          onClose={closeReactionMenu}
          PaperProps={{ sx: { borderRadius: 3, p: 0.5 } }}
        >
          <Box sx={{ display: 'flex', gap: 0.35, px: 0.5, py: 0.25 }}>
            {REACTION_OPTIONS.map((emoji) => (
              <Tooltip key={emoji} title={`React ${emoji}`} arrow>
                <span>
                  <IconButton
                    size="small"
                    disabled={savingReaction}
                    onClick={() => applyMessageReaction(emoji)}
                    sx={{
                      width: 36,
                      height: 36,
                      fontSize: 20,
                      '&:hover': { bgcolor: '#dcfce7', transform: 'translateY(-1px)' },
                    }}
                  >
                    <Box component="span" role="img" aria-label={`Reaction ${emoji}`}>
                      {emoji}
                    </Box>
                  </IconButton>
                </span>
              </Tooltip>
            ))}
          </Box>
          {getMessageReactions(reactionMessage).length > 0 && [
            <Divider key="divider" sx={{ my: 0.5 }} />,
            <MenuItem key="remove" disabled={savingReaction} onClick={() => applyMessageReaction('', true)}>
              <ListItemIcon><CloseIcon fontSize="small" /></ListItemIcon>
              <ListItemText><T>Remove reaction</T></ListItemText>
            </MenuItem>,
          ]}
        </Menu>
      </Box>

      <CustomerRecognitionBar
        conversationId={conversationId}
        canEdit={canSend}
        canMerge={isAdmin || hasPermission('manage_communication')}
        refreshKey={customerBarKey}
        onOpenConversation={onOpenConversation ? (conv) => onOpenConversation({ conversation_id: conv.id }) : null}
        onOpenRepair={(repair) => navigate(repair.process_id ? `/repair-processes/${repair.process_id}` : `/repair-processes?search=${encodeURIComponent(repair.repair_id || '')}`)}
        onLinked={() => {
          if (typeof onConversationUpdated === 'function') onConversationUpdated();
          silentRefresh(messageChannelFilter);
        }}
      />

      <Dialog
        open={showCloseConversationConfirm}
        onClose={() => {
          if (!closingConversation) setShowCloseConversationConfirm(false);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pb: 1 }}>
          <CloseIcon sx={{ color: '#dc2626' }} />
          <Typography fontWeight={800} fontSize={18}><T>Close conversation?</T></Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography color="text.secondary" fontSize={14}><T>
            This chat will be closed and removed from open work. You can reopen it later from the closed conversations filter.
          </T></Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setShowCloseConversationConfirm(false)}
            disabled={closingConversation}
            color="inherit"
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
          ><T>
            Cancel
          </T></Button>
          <Button
            onClick={confirmCloseConversation}
            disabled={closingConversation}
            variant="contained"
            color="error"
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
          >
            {closingConversation ? 'Closing...' : 'Close conversation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Chat Work State ─── */}
      <Box sx={{
        px: 2,
        py: 0.75,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: '#fbfdff',
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        flexWrap: 'wrap',
        flexShrink: 0,
      }}>
        <Button
          type="button"
          size="small"
          onClick={openConversationTriageDialog}
          sx={{
            minHeight: 25,
            borderRadius: 4,
            px: 1.2,
            py: 0.1,
            textTransform: 'none',
            fontSize: 11,
            fontWeight: 900,
            bgcolor: conversationStatusStyle.bg,
            color: conversationStatusStyle.text,
            border: `1px solid ${conversationStatusStyle.border}`,
            '&:hover': { bgcolor: conversationStatusStyle.bg, borderColor: conversationStatusStyle.border },
          }}
        ><T>
          Status: </T>{conversationTriage.status ? statusLabel(conversationTriage.status) : 'Unread'}
        </Button>
        <Button
          type="button"
          size="small"
          onClick={openConversationTriageDialog}
          sx={{
            minHeight: 25,
            borderRadius: 4,
            px: 1.2,
            py: 0.1,
            textTransform: 'none',
            fontSize: 11,
            fontWeight: 900,
            bgcolor: '#f0fdf4',
            color: '#166534',
            border: '1px solid #bbf7d0',
            '&:hover': { bgcolor: '#dcfce7' },
          }}
        ><T>
          Tags</T>{conversationTags.length ? `: ${conversationTags.length}` : ''}
        </Button>
        <Button
          type="button"
          size="small"
          onClick={() => canReadTasks ? setTaskSource({ kind: 'conversation', source_id: conversationId }) : openConversationTriageDialog()}
          sx={{
            minHeight: 25,
            borderRadius: 4,
            px: 1.2,
            py: 0.1,
            textTransform: 'none',
            fontSize: 11,
            fontWeight: 900,
            bgcolor: conversationTask.status === 'closed' ? '#dcfce7' : '#fff7ed',
            color: conversationTask.status === 'closed' ? '#166534' : '#c2410c',
            border: `1px solid ${conversationTask.status === 'closed' ? '#86efac' : '#fed7aa'}`,
            '&:hover': { bgcolor: conversationTask.status === 'closed' ? '#dcfce7' : '#ffedd5' },
          }}
        >
          {canReadTasks ? 'Team tasks' : 'Historic task'}
        </Button>
        <Box sx={{ flex: 1, minWidth: 160, display: 'flex', alignItems: 'center' }}>
          {renderConversationTriageChips()}
        </Box>
      </Box>

      {!emailOnly && (
      <Box sx={{ px: 2, py: 0.85, borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#fff', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', gap: 0.6, overflowX: 'auto', pb: 0.15, scrollbarWidth: 'thin' }}>
          {MESSAGE_FILTER_LIST.map(({ key, label, Icon }) => {
            const active = messageChannelFilter === key;
            const colors = key ? (CHANNEL_COLORS[key] || { main: '#64748b', bg: '#f8fafc', border: '#cbd5e1' }) : { main: '#166534', bg: '#f0fdf4', border: GREEN };
            return (
              <Chip
                key={key || 'all'}
                icon={<Icon sx={{ fontSize: 14, color: active ? `${colors.main} !important` : undefined }} />}
                label={label}
                size="small"
                variant={active ? 'filled' : 'outlined'}
                onClick={() => handleMessageFilterChange(key)}
                sx={{
                  flexShrink: 0,
                  height: 26,
                  fontSize: 12,
                  fontWeight: active ? 800 : 600,
                  cursor: 'pointer',
                  borderRadius: 4,
                  ...(active ? {
                    bgcolor: colors.bg,
                    color: colors.main,
                    border: `1.5px solid ${colors.border}`,
                  } : {
                    color: '#64748b',
                    borderColor: '#e2e8f0',
                    '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' },
                  }),
                }}
              />
            );
          })}
        </Box>
      </Box>
      )}

      {/* ─── Messages ─── */}
      <Box
        ref={messagesContainerRef}
        onScroll={handleMessagesScroll}
        sx={{
          flex: 1, overflowY: 'auto', px: 2.5, py: 2.5, bgcolor: '#f0f2f5',
          display: 'flex', flexDirection: 'column', gap: 0.75,
        }}
      >
        {notice?.message && (
          <Alert severity={notice.severity || 'info'} sx={{ mb: 1, borderRadius: 2 }}>
            {notice.message}
          </Alert>
        )}
        {/* Recognise a returning customer before answering: when they last
            got in touch, on which channel and what about. Content stays in
            the older thread, behind its own permissions. */}
        {conversationId && (
          <Box sx={{ mb: 1 }}>
            <PriorContactPanel conversationId={conversationId} />
          </Box>
        )}
        {isAdmin && mailPolicy && (
          <Box sx={{ mb: 1 }}>
            {mailPolicy.locked && (
              <Alert severity="warning" sx={{ mb: 0.75, borderRadius: 2 }}>
                <Typography sx={{ fontWeight: 800, fontSize: 13 }}><T>Administrator lock is active</T></Typography>
                <Typography variant="body2"><T>This conversation is restricted to administrators by the mail access policy.</T></Typography>
              </Alert>
            )}
            <Alert severity="info" icon={<TaskAltIcon fontSize="small" />} sx={{ borderRadius: 2, py: 0 }}>
              <Typography sx={{ fontWeight: 800, fontSize: 12.5 }}><T>Mail access checklist</T></Typography>
              <Typography variant="caption" component="div"><T>Area: </T>{mailPolicy.area || 'Unassigned'}<T> · Classification: </T>{mailPolicy.source || 'Pending'}<T> · Lock check: </T>{mailPolicy.locked ? 'Restricted' : 'Clear'}</Typography>
            </Alert>
          </Box>
        )}
        {isAdmin && mailPolicyError && (
          <Alert severity="warning" sx={{ mb: 1, borderRadius: 2 }}><T>
            Mail access status could not be loaded. Check the administrator policy before changing access.
          </T></Alert>
        )}
        {hasTranslations && (
          <Alert
            severity="info"
            icon={<TranslateIcon fontSize="small" />}
            action={
              <Button
                size="small"
                onClick={clearInlineTranslation}
                sx={{ textTransform: 'none', fontWeight: 900 }}
              >
                {showOriginal ? 'Live translation' : 'Original'}
              </Button>
            }
            sx={{ mb: 1, borderRadius: 2, alignItems: 'center' }}
          >
            {showOriginal ? 'Showing original texts' : `Live translation: ${translationLanguageOption.nativeName} (your account language)`}
          </Alert>
        )}
        {needsHumanReply && (
          <Alert severity="error" sx={{ mb: 1, borderRadius: 2, alignItems: 'center' }}>
            <Typography sx={{ fontWeight: 800, fontSize: 13.5 }}><T>The chatbot could not answer this customer</T></Typography>
            <Typography sx={{ fontSize: 13 }}>
              {needsHumanReply.reason}<T> Please reply personally. This notice disappears after your reply.
            </T></Typography>
          </Alert>
        )}
        {translationOutage && (
          <Alert
            severity="warning"
            onClose={() => setTranslationOutage(null)}
            sx={{ mb: 1, borderRadius: 2, alignItems: 'center' }}
            action={translationOutage.can_send_untranslated ? (
              <Button
                size="small"
                disabled={sending}
                onClick={() => {
                  const retry = translationOutage.retry || {};
                  setTranslationOutage(null);
                  handleSendMessage(null, { ...retry, sendUntranslated: true });
                }}
                sx={{ textTransform: 'none', fontWeight: 800, whiteSpace: 'nowrap' }}
              ><T>
                Send in </T>{LANGUAGE_NAMES[translationOutage.author_language] || 'original language'}
              </Button>
            ) : null}
          >
            <Typography sx={{ fontWeight: 800, fontSize: 13.5 }}><T>Translation is not available</T></Typography>
            <Typography sx={{ fontSize: 13 }}>
              {translationOutage.message}{' '}
              {translationOutage.customer_language && `The customer reads ${LANGUAGE_NAMES[translationOutage.customer_language] || translationOutage.customer_language}. `}<T>
              Your draft is kept.
            </T></Typography>
          </Alert>
        )}
        {translationError && (
          <Alert severity="warning" sx={{ mb: 1 }} action={
            <Button onClick={retryTranslation}><T>Try again</T></Button>
          }><T>
            Live translation failed. Untranslated messages show the original text.
          </T></Alert>
        )}
        {messagesLoading && (
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
            py: 0.75, px: 1.5, borderRadius: 2, bgcolor: '#fff', border: '1px solid #e5e7eb',
          }}>
            <CircularProgress size={15} sx={{ color: GREEN }} />
            <Typography variant="caption" color="text.secondary" fontWeight={600}><T>
              Filtering messages...
            </T></Typography>
          </Box>
        )}

        {anchorError && <Alert severity="warning"><T>{anchorError}</T></Alert>}
        {anchoredMessage && <Box sx={{ border: '2px solid #32cd32', borderRadius: 2, p: 1, mb: 2 }}>
          <Typography fontWeight={700}><T>Original message of the task</T></Typography>{renderMessage(anchoredMessage)}
        </Box>}
        {shownMessages.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <ChatIcon sx={{ fontSize: 40, color: '#d1d5db', mb: 1 }} />
            <Typography color="text.secondary">
              {messageChannelFilter
                ? `No ${activeFilterLabel} messages in this conversation`
                : 'No messages in this conversation yet'}
            </Typography>
          </Box>
        ) : (
          <>
            {shownMessages.map(renderMessage)}
            <div ref={messagesEndRef} />
          </>
        )}
      </Box>

      {/* ─── Compose ─── */}
      <Box component="section" aria-label={at("Write message")} sx={{ borderTop: '1px solid', borderColor: 'divider', bgcolor: 'white', px: 2, py: 1.25, flexShrink: 0 }}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1, borderRadius: 2 }}>
            <T>{error}</T>
          </Alert>
        )}
        {canSend ? (
          <form onSubmit={(e) => {
            e.preventDefault();
            if (schedulePickerOpen) {
              handleScheduleSubmit();
            } else {
              handleSendMessage(e);
            }
          }}>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={handleAttachmentSelect}
          />
          {customerLanguageHint && (
            <Box
              role="status"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75, flexWrap: 'wrap' }}
            >
              <TranslateIcon sx={{ fontSize: 15, color: customerLanguageHint.translates ? '#0369a1' : '#64748b' }} />
              <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: customerLanguageHint.translates ? '#0369a1' : '#475569' }}>
                {customerLanguageHint.translates
                  ? `Sends in ${languageName(customerLanguageHint.language)}: your text is translated automatically`
                  : `Sends in ${languageName(customerLanguageHint.language)}, as written`}
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                {LANGUAGE_SOURCE_TEXT[customerLanguageHint.source] || ''}
              </Typography>
              {customerId && (
                <Button
                  size="small"
                  onClick={() => setActorDialogOpen(true)}
                  sx={{ minWidth: 0, p: 0, px: 0.5, textTransform: 'none', fontSize: 12, fontWeight: 800, color: '#15803d' }}
                ><T>
                  Change
                </T></Button>
              )}
            </Box>
          )}
          {/* Channel pills */}
          {!emailOnly && (
          <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {CHANNEL_PILL_LIST.map(({ key, label, Icon }) => {
              const active = selectedChannel === key;
              const colors = CHANNEL_COLORS[key] || { main: '#9e9e9e', bg: '#f5f5f5', border: '#9e9e9e' };
              return (
                <Chip
                  key={key}
                  icon={<Icon sx={{ fontSize: 14, color: active ? `${colors.main} !important` : undefined }} />}
                  label={label}
                  size="small"
                  variant={active ? 'filled' : 'outlined'}
                  onClick={() => setSelectedChannel(key)}
                  sx={{
                    fontSize: 12, fontWeight: active ? 700 : 500, cursor: 'pointer',
                    borderRadius: 4, transition: 'all 0.15s',
                    ...(active ? {
                      bgcolor: colors.bg, color: colors.main,
                      borderColor: colors.border, border: `1.5px solid ${colors.border}`,
                    } : {
                      borderColor: '#e0e0e0', color: '#65676b',
                      '&:hover': { borderColor: '#bdbdbd', bgcolor: '#fafafa' },
                    }),
                  }}
                />
              );
            })}
            {scheduledMessages.length > 0 && (
              <Chip
                icon={<ScheduleIcon sx={{ fontSize: 14, color: '#166534 !important' }} />}
                label={`${scheduledMessages.length} scheduled`}
                size="small"
                onClick={() => setShowScheduledDialog(true)}
                sx={{
                  fontSize: 12, fontWeight: 800, cursor: 'pointer', borderRadius: 4,
                  bgcolor: '#dcfce7', color: '#166534', borderColor: '#86efac', border: '1.5px solid #86efac',
                }}
              />
            )}
            {!emailOnly && <Box sx={{ flex: 1, minWidth: 8 }} />}
            {!emailOnly && (
            <Tooltip title={at("Schedule sending")}>
              <IconButton
                type="button"
                size="small"
                onClick={() => {
                  if (!schedulePickerOpen) {
                    const d = new Date();
                    d.setMinutes(d.getMinutes() + 5);
                    setPlannedDateTime(getLocalISOString(d));
                  }
                  setSchedulePickerOpen(!schedulePickerOpen);
                }}
                sx={{
                  width: 32, height: 32, borderRadius: 2, border: '1px solid',
                  color: schedulePickerOpen ? '#15803d' : '#475569',
                  borderColor: schedulePickerOpen ? '#86efac' : '#cbd5e1',
                  bgcolor: schedulePickerOpen ? '#f0fdf4' : '#fff',
                  '&:hover': { borderColor: GREEN, bgcolor: '#f0fdf4', color: '#15803d' },
                }}
              >
                <ScheduleIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            )}
            {!emailOnly && (
            <Tooltip title={translatingConversation ? 'Live translation running…' : `Live translation: ${translationLanguageOption.nativeName} (your account language)`}>
              <span>
                <IconButton
                  type="button"
                  size="small"
                  onClick={handleTranslateConversation}
                  disabled={!conversationId || translatingConversation}
                  sx={{
                    width: 32, height: 32, borderRadius: 2, border: '1px solid #bae6fd',
                    color: '#075985', bgcolor: '#f0f9ff',
                    '&:hover': { borderColor: '#0ea5e9', bgcolor: '#e0f2fe' },
                    '&.Mui-disabled': { opacity: 0.4 },
                  }}
                >
                  {translatingConversation ? <CircularProgress size={15} color="inherit" /> : <TranslateIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </span>
            </Tooltip>
            )}

            {!emailOnly && hasTranslations && (
              <Button
                type="button"
                size="small"
                variant="outlined"
                onClick={clearInlineTranslation}
                sx={{
                  height: 28,
                  minWidth: 0,
                  px: 1,
                  borderRadius: 4,
                  textTransform: 'none',
                  fontSize: 12,
                  fontWeight: 800,
                  color: '#475569',
                  borderColor: '#cbd5e1',
                  bgcolor: '#fff',
                  '&:hover': { bgcolor: '#f8fafc', borderColor: '#94a3b8' },
                }}
              >
                {showOriginal ? 'Live translation' : 'Original'}
              </Button>
            )}
            <Tooltip title={at("Proofread message")}>
              <span>
                <IconButton
                  type="button"
                  size="small"
                  onClick={handleProofread}
                  disabled={proofreading || !newMessage.trim()}
                  sx={{
                    width: 32, height: 32, borderRadius: 2, border: '1px solid #bae6fd',
                    color: '#0369a1', bgcolor: '#f0f9ff',
                    '&:hover': { borderColor: '#0ea5e9', bgcolor: '#e0f2fe' },
                    '&.Mui-disabled': { opacity: 0.4 },
                  }}
                >
                  {proofreading ? <CircularProgress size={15} color="inherit" /> : <SpellcheckIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </span>
            </Tooltip>
            {!emailOnly && (
            <Tooltip title={at("AI reply suggestion")}>
              <span>
                <IconButton
                  type="button"
                  size="small"
                  onClick={handleSuggestReply}
                  disabled={suggestingReply || !conversationId}
                  sx={{
                    width: 32, height: 32, borderRadius: 2, border: '1px solid #ddd6fe',
                    color: '#6d28d9', bgcolor: '#faf5ff',
                    '&:hover': { borderColor: '#8b5cf6', bgcolor: '#f3e8ff' },
                    '&.Mui-disabled': { opacity: 0.4 },
                  }}
                >
                  {suggestingReply ? <CircularProgress size={15} color="inherit" /> : <AutoFixHighIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </span>
            </Tooltip>
            )}
            {!emailOnly && (
            <Tooltip title={at("Templates")}>
              <IconButton
                type="button"
                size="small"
                onClick={(e) => setTemplateAnchor(e.currentTarget)}
                sx={{
                  width: 32, height: 32, borderRadius: 2, border: '1px solid #bbf7d0',
                  color: '#166534', bgcolor: '#f0fdf4',
                  '&:hover': { borderColor: GREEN, bgcolor: '#dcfce7' },
                }}
              >
                <TemplateIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            )}
            {!emailOnly && (
            <Box sx={{
              height: 28,
              display: 'flex',
              alignItems: 'center',
              gap: 0.4,
              pl: 0.9,
              pr: 0.15,
              border: '1px solid #e2e8f0',
              borderRadius: 4,
              bgcolor: '#fff',
            }}>
              <LanguageIcon sx={{ fontSize: 15, color: '#64748b' }} />
              <Select
                value={templateLanguage}
                onChange={(e) => setTemplateLanguage(e.target.value)}
                variant="standard"
                disableUnderline
                renderValue={() => templateLanguageLabel}
                sx={{
                  minWidth: 45,
                  fontSize: 12,
                  fontWeight: 800,
                  color: '#0f172a',
                  '& .MuiSelect-select': { py: 0, pr: '20px !important' },
                }}
              >
                {TEMPLATE_LANGUAGES.map((language) => (
                  <MenuItem key={language.key} value={language.key}>
                    <ListItemText
                      primary={language.label}
                      secondary={language.name}
                      primaryTypographyProps={{ fontWeight: 800, fontSize: 13 }}
                      secondaryTypographyProps={{ fontSize: 11 }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </Box>
            )}
          </Box>
          )}

          {!emailOnly && customerNotOnWhatsApp && selectedChannel === ChannelType.WHATSAPP && (
            <Alert
              severity="warning"
              sx={{ mb: 1, py: 0.5, borderRadius: 2, fontSize: 13 }}
              icon={<WhatsAppIcon fontSize="small" />}
            ><T>
              ⚠️ WhatsApp could not deliver a previous message to this customer. They may have blocked the number, have no WhatsApp session, or need to message us first. Consider </T><strong><T>SMS</T></strong><T>, email, or a call.
            </T></Alert>
          )}

          {pendingAttachments.length > 0 && (
            <Box sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.75,
              mb: 1,
              p: 0.85,
              borderRadius: 2,
              border: '1px solid #dbeafe',
              bgcolor: '#f8fafc',
            }}>
              {pendingAttachments.map((item) => {
                const Icon = attachmentIcon(item.contentType);
                return (
                  <Chip
                    key={item.id}
                    icon={<Icon sx={{ fontSize: 14 }} />}
                    label={`${item.name}${item.size ? ` · ${formatFileSize(item.size)}` : ''}`}
                    onDelete={() => removePendingAttachment(item.id)}
                    size="small"
                    sx={{
                      maxWidth: 300,
                      height: 28,
                      borderRadius: 3,
                      bgcolor: '#fff',
                      border: '1px solid #e2e8f0',
                      '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                    }}
                  />
                );
              })}
            </Box>
          )}

          {!emailOnly && (recording || recordedAudio) && (
            <Box sx={{
              display: 'flex',
              alignItems: { xs: 'stretch', sm: 'center' },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1,
              mb: 1,
              p: 1,
              borderRadius: 2.5,
              border: `1px solid ${recording ? '#fecaca' : '#bbf7d0'}`,
              bgcolor: recording ? '#fef2f2' : '#f0fdf4',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                <VoiceNoteIcon sx={{ fontSize: 20, color: recording ? '#dc2626' : '#166534' }} />
                {recording ? (
                  <Typography fontSize={13} fontWeight={800} sx={{ color: '#991b1b' }}><T>
                    Recording... </T>{formatDuration(recordingSeconds)}
                  </Typography>
                ) : (
                  <>
                    <Box
                      component="audio"
                      src={recordedAudio.previewUrl}
                      controls
                      sx={{ width: { xs: '100%', sm: 260 }, height: 34 }}
                    />
                    <Chip
                      label={formatDuration(recordedAudio.duration)}
                      size="small"
                      sx={{ height: 22, fontSize: 11, bgcolor: '#dcfce7', color: '#166534', fontWeight: 800 }}
                    />
                  </>
                )}
              </Box>

              {recording ? (
                <Button
                  type="button"
                  variant="contained"
                  size="small"
                  startIcon={<StopCircleIcon />}
                  onClick={stopAudioRecording}
                  sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 800, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}
                ><T>
                  Stop
                </T></Button>
              ) : (
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', justifyContent: { xs: 'flex-end', sm: 'initial' } }}>
                  <Button
                    type="button"
                    size="small"
                    variant="outlined"
                    startIcon={<AutoFixHighIcon />}
                    onClick={handleTranscribeRecordedAudio}
                    disabled={processingAudio || transcribingAudio || sending}
                    sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 800, borderColor: '#bbf7d0', color: '#166534' }}
                  >
                    {transcribingAudio ? 'Transcribing...' : processingAudio ? 'Enhancing...' : 'Text + enhance'}
                  </Button>
                  <Button
                    type="button"
                    size="small"
                    variant="contained"
                    startIcon={<VoiceNoteIcon />}
                    onClick={handleSendRecordedAudio}
                    disabled={processingAudio || transcribingAudio || sending}
                    sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 800, bgcolor: GREEN, color: '#052e16', '&:hover': { bgcolor: '#28a428' } }}
                  ><T>
                    Send vocal
                  </T></Button>
                  <IconButton
                    type="button"
                    size="small"
                    onClick={clearRecordedAudio}
                    disabled={processingAudio || transcribingAudio || sending}
                    sx={{ width: 32, height: 32, border: '1px solid #e2e8f0', bgcolor: '#fff' }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              )}
              {!recording && recordedAudio && (
                <Box sx={{
                  flexBasis: '100%',
                  width: '100%',
                  borderTop: '1px solid #bbf7d0',
                  pt: 0.75,
                  mt: { xs: 0, sm: 0.25 },
                  display: 'grid',
                  gap: 0.5,
                }}>
                  {transcribingAudio ? (
                    <Typography fontSize={12} fontWeight={800} color="#166534"><T>
                      Transcribing original speech...
                    </T></Typography>
                  ) : recordedAudio.transcript ? (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                        <Typography fontSize={12} fontWeight={900} color="#166534"><T>
                          Original speech
                        </T></Typography>
                        {recordedAudio.transcriptLanguage && (
                          <Chip
                            label={recordedAudio.transcriptLanguage}
                            size="small"
                            sx={{ height: 18, fontSize: 10, bgcolor: '#dcfce7', color: '#166534', fontWeight: 800 }}
                          />
                        )}
                        <Button
                          type="button"
                          size="small"
                          variant="text"
                          onClick={() => applyAudioTextToComposer(recordedAudio.transcript)}
                          sx={{ minHeight: 22, py: 0, px: 0.75, borderRadius: 2, textTransform: 'none', fontSize: 12, fontWeight: 900, color: '#166534' }}
                        ><T>
                          Use original text
                        </T></Button>
                      </Box>
                      <Typography
                        fontSize={12}
                        color="#334155"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {recordedAudio.transcript}
                      </Typography>
                    </>
                  ) : (
                    <Typography fontSize={12} color="text.secondary"><T>
                      No speech text detected yet. You can still send the vocal message.
                    </T></Typography>
                  )}
                </Box>
              )}
            </Box>
          )}

          {schedulePickerOpen && (
            <Paper elevation={0} sx={{ p: 1.5, mb: 1, border: '1.5px solid #86efac', bgcolor: '#f0fdf4', borderRadius: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography fontSize={13} fontWeight={800} color="#14532d" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <ScheduleIcon sx={{ fontSize: 18, color: '#16a34a' }} /><T>
                  Plan when to send
                </T></Typography>
                <IconButton size="small" onClick={() => setSchedulePickerOpen(false)}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
              {/* ── Channel selector for scheduled message ── */}
              <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography fontSize={11} fontWeight={700} color="#475569" sx={{ mr: 0.5 }}><T>Channel:</T></Typography>
                {CHANNEL_PILL_LIST.map(({ key, label, Icon }) => {
                  const active = selectedChannel === key;
                  const colors = CHANNEL_COLORS[key] || { main: '#9e9e9e', bg: '#f5f5f5', border: '#9e9e9e' };
                  return (
                    <Chip
                      key={key}
                      icon={<Icon sx={{ fontSize: 13, color: active ? `${colors.main} !important` : undefined }} />}
                      label={label}
                      size="small"
                      variant={active ? 'filled' : 'outlined'}
                      onClick={() => setSelectedChannel(key)}
                      sx={{
                        fontSize: 11, fontWeight: active ? 700 : 500, cursor: 'pointer',
                        borderRadius: 4, transition: 'all 0.15s', height: 24,
                        ...(active ? {
                          bgcolor: colors.bg, color: colors.main,
                          borderColor: colors.border, border: `1.5px solid ${colors.border}`,
                        } : {
                          borderColor: '#cbd5e1', color: '#64748b',
                          '&:hover': { borderColor: colors.main, color: colors.main },
                        }),
                      }}
                    />
                  );
                })}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  type="datetime-local"
                  size="small"
                  label={at("Send date and time (DD/MM/YYYY)")}
                  value={plannedDateTime}
                  onChange={(e) => setPlannedDateTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: getLocalISOString(new Date()),
                    step: 60
                  }}
                  sx={{
                    bgcolor: '#fff', flex: 1, minWidth: 240,
                    '& .MuiOutlinedInput-root': { borderRadius: 2 }
                  }}
                />
                <Button
                  type="button"
                  variant="contained"
                  disabled={scheduling || (!newMessage.trim() && pendingAttachments.length === 0) || !plannedDateTime}
                  onClick={handleScheduleSubmit}
                  startIcon={scheduling ? <CircularProgress size={16} color="inherit" /> : <ScheduleIcon />}
                  sx={{
                    bgcolor: '#16a34a', color: '#fff', fontWeight: 800, textTransform: 'none', borderRadius: 2,
                    '&:hover': { bgcolor: '#15803d' }
                  }}
                >
                  {scheduling ? 'Scheduling…' : 'Schedule message'}
                </Button>
              </Box>
              <Typography fontSize={11} color="text.secondary" sx={{ mt: 0.75 }}><T>
                The message is sent automatically at the chosen time via </T><strong>{selectedChannel || 'Email'}</strong><T>. You can cancel it at any time before it is sent.
              </T></Typography>
            </Paper>
          )}

          {/* ── AI Response Assistant Suggestion Card ── */}
          {aiDraft && (
            <Paper elevation={0} sx={{ p: 2, mb: 1.5, border: '2px solid #16a34a', bgcolor: '#f0fdf4', borderRadius: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography fontSize={13} fontWeight={800} color="#166534" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <AutoFixHighIcon sx={{ fontSize: 18, color: '#16a34a' }} /><T>
                  🤖 AI Response Suggestion (RAG + Knowledge Base)
                </T></Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleApplyAiDraft}
                    sx={{ bgcolor: '#16a34a', color: '#fff', fontWeight: 800, textTransform: 'none', borderRadius: 2, fontSize: 12, '&:hover': { bgcolor: '#15803d' } }}
                  ><T>
                    Use in Composer
                  </T></Button>
                  <IconButton size="small" onClick={() => { setAiDraft(null); setAiFeedbackCategory(null); }}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Box>

              {/* Sources used */}
              {(aiDraft.rag_sources || []).length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                  {aiDraft.rag_sources.map((src, idx) => (
                    <Chip
                      key={idx}
                      label={src.title || src.summary || src.type}
                      size="small"
                      sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: '#dcfce7', color: '#166534', border: '1px solid #86efac' }}
                    />
                  ))}
                </Box>
              )}

              <Typography fontSize={13} color="#1e293b" sx={{ whiteSpace: 'pre-wrap', bgcolor: '#fff', p: 1.5, borderRadius: 2, border: '1px solid #bbf7d0' }}>
                {aiDraft.ai_draft_text}
              </Typography>

              {/* Structured feedback — trains the assistant on WHY you edit (optional). */}
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                <Typography fontSize={11} fontWeight={700} color="#166534" sx={{ mr: 0.5 }}><T>
                  Feedback for learning:
                </T></Typography>
                {[
                  { key: 'EXCELLENT', label: 'Good' },
                  { key: 'WRONG_TONE', label: 'Wrong tone' },
                  { key: 'MISSING_DETAILS', label: 'Missing details' },
                  { key: 'INCORRECT_INFO', label: 'Incorrect info' },
                  { key: 'OUTDATED_PRICE', label: 'Outdated price' },
                ].map((f) => (
                  <Chip
                    key={f.key}
                    label={f.label}
                    size="small"
                    onClick={() => setAiFeedbackCategory((c) => (c === f.key ? null : f.key))}
                    sx={{
                      height: 22, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                      bgcolor: aiFeedbackCategory === f.key ? '#16a34a' : '#fff',
                      color: aiFeedbackCategory === f.key ? '#fff' : '#166534',
                      border: '1px solid #86efac',
                    }}
                  />
                ))}
                <Typography fontSize={10} color="#65a30d" sx={{ ml: 0.5 }}><T>
                  captured when you send
                </T></Typography>
              </Box>
            </Paper>
          )}

          {!emailOnly && slashTemplateMatches.length > 0 && (
            <Box sx={{
              mb: 1,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              p: 0.75,
              borderRadius: 2,
              border: '1px solid #dbeafe',
              bgcolor: '#eff6ff',
            }}>
              {slashTemplateMatches.map((template) => (
                <Chip
                  key={template.key}
                  icon={<TemplateIcon sx={{ fontSize: 13 }} />}
                  label={`/${template.command}`}
                  size="small"
                  onClick={() => applyTemplate(template)}
                  sx={{
                    height: 24,
                    fontSize: 12,
                    fontWeight: 700,
                    bgcolor: '#fff',
                    color: '#1d4ed8',
                    border: '1px solid #bfdbfe',
                    cursor: 'pointer',
                  }}
                />
              ))}
            </Box>
          )}

          {/* Input row */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            {!emailOnly && (
            <IconButton
              type="button"
              onClick={recording ? stopAudioRecording : startAudioRecording}
              disabled={sending || processingAudio}
              sx={{
                width: 42,
                height: 42,
                border: '1px solid',
                borderColor: recording ? '#fecaca' : '#e2e8f0',
                bgcolor: recording ? '#fee2e2' : '#fff',
                color: recording ? '#dc2626' : '#166534',
                '&:hover': { bgcolor: recording ? '#fecaca' : '#f0fdf4', borderColor: recording ? '#f87171' : GREEN },
              }}
            >
              {recording ? <StopCircleIcon sx={{ fontSize: 21 }} /> : <MicIcon sx={{ fontSize: 20 }} />}
            </IconButton>
            )}
            <Tooltip title={at("Generate AI Response Suggestion (RAG + Knowledge Base)")}>
              <IconButton
                type="button"
                onClick={handleGenerateAiDraft}
                disabled={aiDraftLoading || sending}
                sx={{
                  width: 42,
                  height: 42,
                  border: '1px solid #bbf7d0',
                  bgcolor: '#f0fdf4',
                  color: '#16a34a',
                  '&:hover': { bgcolor: '#dcfce7', borderColor: '#16a34a' },
                }}
              >
                {aiDraftLoading ? <CircularProgress size={18} color="success" /> : <AutoFixHighIcon sx={{ fontSize: 20 }} />}
              </IconButton>
            </Tooltip>
            <IconButton
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending}
              sx={{
                width: 42,
                height: 42,
                border: '1px solid #e2e8f0',
                bgcolor: '#fff',
                color: '#475569',
                '&:hover': { bgcolor: '#f8fafc', borderColor: GREEN, color: '#166534' },
              }}
            >
              <AttachFileIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <TextField
              inputRef={messageInputRef}
              multiline
              minRows={1}
              maxRows={12}
              placeholder={schedulePickerOpen ? "Type your scheduled message..." : "Type your message..."}
              value={newMessage}
              onChange={(e) => handleNewMessageChange(e.target.value)}
              disabled={sending || scheduling}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if ((newMessage.trim() || pendingAttachments.length > 0) && !sending && !scheduling) {
                    if (schedulePickerOpen) {
                      handleScheduleSubmit();
                    } else {
                      handleSendMessage(e);
                    }
                  }
                }
              }}
              fullWidth
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 5, bgcolor: '#f0f2f5', fontSize: 14,
                  '&.Mui-focused': { bgcolor: 'white' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: schedulePickerOpen ? '#16a34a' : GREEN },
                },
              }}
            />
            <Tooltip title={schedulePickerOpen ? `Schedule message for ${formatDDMMYYYY(plannedDateTime) || 'the chosen time'}` : 'Send message now'}>
              <span>
                <IconButton
                  type="submit"
                  aria-label={schedulePickerOpen ? 'Schedule message' : 'Send message now'}
                  disabled={(!newMessage.trim() && pendingAttachments.length === 0) || sending || scheduling || (schedulePickerOpen && !plannedDateTime)}
                  sx={{
                    width: 42, height: 42,
                    background: schedulePickerOpen
                      ? 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)'
                      : `linear-gradient(135deg, ${GREEN} 0%, #28a428 100%)`,
                    color: 'white', boxShadow: schedulePickerOpen ? '0 2px 6px rgba(22,163,74,0.3)' : `0 2px 6px rgba(50,205,50,0.3)`,
                    '&:hover': { transform: 'scale(1.05)', boxShadow: schedulePickerOpen ? '0 3px 10px rgba(22,163,74,0.4)' : `0 3px 10px rgba(50,205,50,0.4)` },
                    '&:disabled': { opacity: 0.4, background: '#bdbdbd', color: 'white', transform: 'none', boxShadow: 'none' },
                    transition: 'all 0.2s',
                  }}
                >
                  {(sending || scheduling) ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : schedulePickerOpen ? (
                    <ScheduleIcon sx={{ fontSize: 19 }} />
                  ) : (
                    <SendIcon sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          </form>
        ) : (
          <Alert severity="info" sx={{ borderRadius: 2, fontWeight: 700 }}><T>
            Read-only: You do not have permission to send messages in this conversation.
          </T></Alert>
        )}
      </Box>

      <Menu
        anchorEl={templateAnchor}
        open={Boolean(templateAnchor)}
        onClose={() => {
          setTemplateAnchor(null);
          setTemplateSearch('');
        }}
        PaperProps={{
          sx: {
            width: { xs: 'calc(100vw - 32px)', sm: 460 },
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: 540,
            borderRadius: 2,
            p: 1,
          },
        }}
      >
        <Box sx={{ px: 1, pt: 0.5, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <TemplateIcon sx={{ fontSize: 18, color: '#166534' }} />
            <Typography fontSize={14} fontWeight={800}><T>Message Templates</T></Typography>
            <Chip
              label={templateLanguageLabel}
              size="small"
              sx={{ ml: 'auto', height: 20, fontSize: 11, fontWeight: 800, bgcolor: '#dcfce7', color: '#166534' }}
            />
            {selectedChannel !== ChannelType.WHATSAPP ? (
              <Chip
                label={at("Local templates")}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: 10, fontWeight: 700 }}
              />
            ) : twilioTemplatesLoading ? (
              <CircularProgress size={15} />
            ) : (
              <Chip
                label={twilioTemplatesLoaded ? 'Twilio live' : 'Local fallback'}
                size="small"
                color={twilioTemplatesLoaded ? 'success' : 'default'}
                variant="outlined"
                sx={{ height: 20, fontSize: 10, fontWeight: 700 }}
              />
            )}
          </Box>
          {selectedChannel === ChannelType.WHATSAPP && twilioTemplatesError && (
            <Alert severity="warning" sx={{ mb: 1, py: 0, fontSize: 11 }}>
              <T>{twilioTemplatesError}</T><T> The local list stays available.
            </T></Alert>
          )}
          <TextField
            size="small"
            fullWidth
            placeholder={at("Search templates...")}
            value={templateSearch}
            onChange={(e) => setTemplateSearch(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: 13,
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: GREEN },
              },
            }}
          />
        </Box>
        <Divider />
        <Box sx={{ maxHeight: 390, overflowY: 'auto', py: 0.5 }}>
          {filteredTemplates.length === 0 ? (
            <Typography fontSize={13} color="text.secondary" sx={{ px: 2, py: 2 }}><T>
              No approved WhatsApp templates found for this language.
            </T></Typography>
          ) : filteredTemplates.map((template) => {
            const body = getTemplateBody(template);
            const preview = body.split('\n').find((line) => line.trim()) || body;

            return (
              <MenuItem
                key={template.key}
                onClick={() => applyTemplate(template)}
                sx={{
                  alignItems: 'flex-start',
                  borderRadius: 1.5,
                  py: 1,
                  px: 1.25,
                  gap: 1,
                }}
              >
                <ListItemIcon sx={{ minWidth: 28, mt: 0.25 }}>
                  <TemplateIcon sx={{ fontSize: 17, color: '#16a34a' }} />
                </ListItemIcon>
                <ListItemText
                  primary={(
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Typography fontSize={13} fontWeight={800}>{template.label}</Typography>
                      <Chip
                        label={`/${template.command}`}
                        size="small"
                        sx={{ height: 18, fontSize: 10, bgcolor: '#eef2ff', color: '#3730a3', fontWeight: 700 }}
                      />
                      {template.whatsappCategory && (
                        <Chip
                          label={template.whatsappCategory}
                          size="small"
                          variant="outlined"
                          sx={{ height: 18, fontSize: 9, fontWeight: 700 }}
                        />
                      )}
                    </Box>
                  )}
                  secondary={(
                    <Typography
                      component="span"
                      fontSize={12}
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        mt: 0.35,
                      }}
                    >
                      {preview}
                    </Typography>
                  )}
                />
              </MenuItem>
            );
          })}
        </Box>
      </Menu>

      {/* ─── Voice Text Review Dialog ─── */}
      <Dialog
        open={audioReviewOpen}
        onClose={() => setAudioReviewOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: `2px solid ${GREEN}`, py: 1.5 }}>
          <AutoFixHighIcon sx={{ color: '#166534' }} />
          <Typography fontWeight={800} fontSize={16}><T>Voice Text + AI Enhance</T></Typography>
          <Box sx={{ flex: 1 }} />
          <IconButton size="small" onClick={() => setAudioReviewOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Autocomplete
              size="small"
              value={audioReviewLanguageOption}
              options={AI_LANGUAGE_OPTIONS}
              getOptionLabel={(option) => `${option.label} - ${option.name}`}
              isOptionEqualToValue={(option, value) => option.key === value.key}
              onChange={handleAudioReviewLanguageChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={at("AI output language")}
                  placeholder={at("Search language...")}
                  helperText={`AI will enhance and translate to ${getAudioLanguageName(audioReviewLanguage)}.`}
                />
              )}
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
              <Box sx={{
                border: '1px solid #dbeafe',
                borderRadius: 2,
                bgcolor: '#f8fbff',
                p: 1.5,
                minHeight: 220,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <MicIcon sx={{ color: '#1d4ed8', fontSize: 18 }} />
                  <Typography fontWeight={900} fontSize={13}><T>Original speech</T></Typography>
                  {audioReview.sourceLanguage && (
                    <Chip
                      label={audioReview.sourceLanguage}
                      size="small"
                      sx={{ height: 20, fontSize: 10, fontWeight: 800, bgcolor: '#dbeafe', color: '#1e3a8a' }}
                    />
                  )}
                </Box>
                <TextField
                  value={audioReview.original}
                  onChange={(e) => setAudioReview((prev) => ({ ...prev, original: e.target.value }))}
                  multiline
                  minRows={7}
                  fullWidth
                  size="small"
                  placeholder={at("Original transcript...")}
                />
                <Button
                  variant="outlined"
                  onClick={() => applyAudioTextToComposer(audioReview.original)}
                  disabled={!audioReview.original.trim()}
                  sx={{ justifySelf: 'start', borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
                ><T>
                  Use original text
                </T></Button>
              </Box>

              <Box sx={{
                border: '1px solid #bbf7d0',
                borderRadius: 2,
                bgcolor: '#f0fdf4',
                p: 1.5,
                minHeight: 220,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <AutoFixHighIcon sx={{ color: '#166534', fontSize: 18 }} />
                  <Typography fontWeight={900} fontSize={13}><T>
                    AI enhanced / translated
                  </T></Typography>
                  <Chip
                    label={audioReviewLanguageOption.label}
                    size="small"
                    sx={{ height: 20, fontSize: 10, fontWeight: 800, bgcolor: '#dcfce7', color: '#166534' }}
                  />
                </Box>
                <TextField
                  value={audioReview.enhanced}
                  onChange={(e) => setAudioReview((prev) => ({ ...prev, enhanced: e.target.value }))}
                  multiline
                  minRows={7}
                  fullWidth
                  size="small"
                  placeholder={processingAudio ? 'Enhancing with AI...' : 'AI result...'}
                  InputProps={{
                    endAdornment: processingAudio ? <CircularProgress size={16} /> : null,
                  }}
                />
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    startIcon={processingAudio ? <CircularProgress size={14} /> : <AutoFixHighIcon />}
                    onClick={() => enhanceAudioReviewText(audioReview.original, audioReview.sourceLanguage, audioReviewLanguage)}
                    disabled={processingAudio || !audioReview.original.trim()}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
                  ><T>
                    Regenerate
                  </T></Button>
                  <Button
                    variant="contained"
                    onClick={() => applyAudioTextToComposer(audioReview.enhanced)}
                    disabled={processingAudio || !audioReview.enhanced.trim()}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 900,
                      bgcolor: GREEN,
                      color: '#052e16',
                      '&:hover': { bgcolor: '#28a428' },
                    }}
                  ><T>
                    Use enhanced text
                  </T></Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button onClick={() => setAudioReviewOpen(false)} color="inherit" sx={{ borderRadius: 2 }}><T>Close</T></Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(taskSource)} onClose={() => setTaskSource(null)} fullWidth maxWidth="md">
        <DialogTitle><T>Team tasks</T></DialogTitle><DialogContent>
          {taskSource && <WorkTaskPanel source={taskSource} relatedSources={conversation?.process_id ? [{ kind: 'repair', source_id: conversation.process_id }] : []} initialTitle={conversation?.subject || ''} />}
        </DialogContent><DialogActions><Button onClick={() => setTaskSource(null)}><T>Close</T></Button></DialogActions>
      </Dialog>
      {/* ─── Conversation Triage Dialog ─── */}
      <Dialog
        open={showConversationTriage}
        onClose={() => setShowConversationTriage(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: `2px solid ${GREEN}`, py: 1.5 }}>
          <DescriptionIcon sx={{ color: '#166534' }} />
          <Typography fontWeight={800} fontSize={16}><T>Chat Status, Tags & Task</T></Typography>
          <Box sx={{ flex: 1 }} />
          <IconButton size="small" onClick={() => setShowConversationTriage(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <TextField
              select
              label={at("Status")}
              size="small"
              value={triageForm.status}
              onChange={(e) => setTriageForm((prev) => ({
                ...prev,
                status: e.target.value,
                task_status: e.target.value === 'task_open'
                  ? 'open'
                  : e.target.value === 'task_closed'
                    ? 'closed'
                    : prev.task_status,
              }))}
            >
              {MESSAGE_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.key || 'none'} value={option.key}>{option.label}</MenuItem>
              ))}
            </TextField>

            <Box>
              <Typography fontSize={12} fontWeight={800} color="text.secondary" sx={{ mb: 0.75 }}><T>
                Tags
              </T></Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {MESSAGE_TAG_OPTIONS.map((tag) => {
                  const active = triageForm.tags.includes(tag);
                  return (
                    <Chip
                      key={tag}
                      label={tag}
                      onClick={() => toggleTriageListValue('tags', tag)}
                      variant={active ? 'filled' : 'outlined'}
                      sx={{
                        fontWeight: 800,
                        bgcolor: active ? '#dcfce7' : '#fff',
                        color: active ? '#166534' : '#475569',
                        borderColor: active ? GREEN : '#cbd5e1',
                        cursor: 'pointer',
                      }}
                    />
                  );
                })}
              </Box>
            </Box>

            <Box>
              <Typography fontSize={12} fontWeight={800} color="text.secondary" sx={{ mb: 0.75 }}><T>
                Contact Category
              </T></Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {CONTACT_CATEGORY_OPTIONS.map((category) => {
                  const active = triageForm.contact_categories.includes(category);
                  return (
                    <Chip
                      key={category}
                      label={category}
                      onClick={() => toggleTriageListValue('contact_categories', category)}
                      variant={active ? 'filled' : 'outlined'}
                      sx={{
                        fontWeight: 800,
                        bgcolor: active ? '#fef3c7' : '#fff',
                        color: active ? '#92400e' : '#475569',
                        borderColor: active ? '#f59e0b' : '#cbd5e1',
                        cursor: 'pointer',
                      }}
                    />
                  );
                })}
              </Box>
            </Box>

            <Divider />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' }, gap: 1.25 }}>
              <TextField
                select
                label={at("Historic task status")}
                disabled={canReadTasks}
                size="small"
                value={triageForm.task_status}
                onChange={(e) => setTriageForm((prev) => ({ ...prev, task_status: e.target.value }))}
              >
                <MenuItem value=""><T>No task</T></MenuItem>
                <MenuItem value="open"><T>Task open</T></MenuItem>
                <MenuItem value="closed"><T>Task closed</T></MenuItem>
              </TextField>
              <TextField
                label={at("Historic task title")}
                disabled={canReadTasks}
                size="small"
                value={triageForm.task_title}
                onChange={(e) => setTriageForm((prev) => ({ ...prev, task_title: e.target.value }))}
                placeholder={at("e.g. Call back, assign mechanic, send offer")}
              />
            </Box>
            <TextField
              label={at("Historic task note")}
              disabled={canReadTasks}
              size="small"
              multiline
              minRows={2}
              value={triageForm.task_note}
              onChange={(e) => setTriageForm((prev) => ({ ...prev, task_note: e.target.value }))}
              helperText={at("New tasks and changes are managed in Team tasks.")}
              placeholder={at("Internal note for this chat...")}
            />

            <Divider />

            <Box sx={{ border: '1px solid #dbeafe', bgcolor: '#f8fbff', borderRadius: 2, p: 1.25 }}>
              <Typography fontSize={12} fontWeight={900} color="#1e3a8a" sx={{ mb: 1 }}><T>
                Repair link
              </T></Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr auto' }, gap: 1 }}>
                <TextField
                  label={at("Search repair tracking number")}
                  size="small"
                  value={repairSearch}
                  onChange={(e) => setRepairSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      loadRepairCandidates(repairSearch, true);
                    }
                  }}
                  placeholder="REP-AUS25"
                />
                <Button
                  variant="outlined"
                  onClick={() => loadRepairCandidates(repairSearch, true)}
                  disabled={repairLookupLoading}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
                >
                  {repairLookupLoading ? <CircularProgress size={16} /> : 'Search'}
                </Button>
              </Box>

              {repairLookupError && (
                <Alert severity="warning" sx={{ mt: 1, py: 0.25 }}><T>{repairLookupError}</T></Alert>
              )}

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                {repairOptions.map((option) => (
                  <Chip
                    key={`${option.repair_id || 'repair'}-${option.process_id || 'process'}`}
                    label={`${option.repair_id || 'Repair'}${option.is_current ? ' · current' : ''}`}
                    onClick={() => applyRepairCandidate(option)}
                    variant={triageForm.repair_id === option.repair_id ? 'filled' : 'outlined'}
                    sx={{
                      fontWeight: 900,
                      bgcolor: triageForm.repair_id === option.repair_id ? '#dcfce7' : '#fff',
                      color: triageForm.repair_id === option.repair_id ? '#166534' : '#1d4ed8',
                      borderColor: triageForm.repair_id === option.repair_id ? GREEN : '#93c5fd',
                      cursor: 'pointer',
                    }}
                  />
                ))}
              </Box>

              {!repairLookupLoading && !repairOptions.length && (
                <Typography fontSize={12} color="text.secondary" sx={{ mt: 1 }}><T>
                  No active repair link found yet. Search by tracking number or keep the manual fields below.
                </T></Typography>
              )}
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25 }}>
              <TextField
                label={at("Repair ID")}
                size="small"
                value={triageForm.repair_id}
                onChange={(e) => setTriageForm((prev) => ({ ...prev, repair_id: e.target.value }))}
                placeholder="REP-XXXXX"
              />
              <TextField
                label={at("Process UUID")}
                size="small"
                value={triageForm.process_id}
                onChange={(e) => setTriageForm((prev) => ({ ...prev, process_id: e.target.value }))}
                placeholder={at("Optional")}
              />
            </Box>
            <Chip
              label={triageForm.apply_repair_to_conversation ? 'Apply repair link to conversation' : 'Keep repair link unchanged'}
              onClick={() => setTriageForm((prev) => ({
                ...prev,
                apply_repair_to_conversation: !prev.apply_repair_to_conversation,
              }))}
              sx={{
                justifySelf: 'start',
                fontWeight: 800,
                bgcolor: triageForm.apply_repair_to_conversation ? '#dcfce7' : '#f8fafc',
                color: triageForm.apply_repair_to_conversation ? '#166534' : '#475569',
                border: `1px solid ${triageForm.apply_repair_to_conversation ? GREEN : '#cbd5e1'}`,
                cursor: 'pointer',
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button onClick={() => setShowConversationTriage(false)} color="inherit" sx={{ borderRadius: 2 }}><T>Cancel</T></Button>
          <Button
            onClick={saveConversationTriage}
            disabled={savingTriage}
            variant="contained"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 800,
              bgcolor: GREEN,
              '&:hover': { bgcolor: '#28a428' },
            }}
          >
            {savingTriage ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Email Preview Dialog ─── */}
      <Dialog open={showEmailModal} onClose={() => setShowEmailModal(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: `2px solid ${GREEN}`, py: 1.5 }}>
          <EmailIcon color="primary" />
          <Typography fontWeight={600} fontSize={16}>{selectedEmail?.subject || 'Email'}</Typography>
          <Box sx={{ flex: 1 }} />
          <IconButton size="small" onClick={() => setShowEmailModal(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedEmail && (
            <>
              <Box sx={{ px: 2.5, py: 1.25, bgcolor: '#fafafa', borderBottom: '1px solid #e0e0e0', fontSize: 13, color: '#666' }}>
                <div><strong><T>From:</T></strong> {selectedEmail.sender_display || 'eScooterLab System'}</div>
                <div><strong><T>Date:</T></strong> {formatTime(selectedEmail.created_at)}</div>
                {selectedEmail.message_metadata?.event_type && (
                  <div><strong><T>Type:</T></strong> {EVENT_TYPE_LABELS[selectedEmail.message_metadata.event_type] || selectedEmail.message_metadata.event_type}</div>
                )}
              </Box>
              <iframe
                title={`email-modal-${selectedEmail.id}`}
                srcDoc={selectedEmail.content_html}
                style={{ width: '100%', minHeight: '500px', border: 'none', display: 'block' }}
                sandbox="allow-same-origin"
                onLoad={(e) => {
                  try {
                    const body = e.target.contentDocument?.body;
                    if (body) {
                      const h = Math.min(Math.max(body.scrollHeight + 30, 400), 700);
                      e.target.style.height = h + 'px';
                    }
                  } catch (err) { /* ignore */ }
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1 }}>
          <Button onClick={() => setShowEmailModal(false)} color="inherit" sx={{ borderRadius: 2 }}><T>Close</T></Button>
        </DialogActions>
      </Dialog>

      {/* ─── Call Recording Dialog ─── */}
      <Dialog open={showRecordingDialog} onClose={() => setShowRecordingDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PlayCircleIcon />
          <Typography fontWeight={700}><T>Call Recording</T></Typography>
          <Box sx={{ flex: 1 }} />
          <IconButton size="small" onClick={() => setShowRecordingDialog(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedCallRecording && (
            <Box sx={{ display: 'grid', gap: 2 }}>
              <Box sx={{ color: '#64748b', fontSize: 13 }}>
                <div><strong><T>Call:</T></strong> {selectedCallRecording.sender_display || 'Customer'} · {formatTime(selectedCallRecording.created_at)}</div>
                <div><strong><T>Duration:</T></strong> {formatDuration(selectedCallRecording.call_recording_duration_seconds || selectedCallRecording.call_duration_seconds)}</div>
              </Box>
              {selectedCallRecording.call_record_id ? (
                <Box
                  component="audio"
                  controls
                  preload="metadata"
                  src={getRecordingProxyUrl(selectedCallRecording.call_record_id)}
                  sx={{ width: '100%' }}
                />
              ) : (
                <Alert severity="warning"><T>Recording reference is missing for this call.</T></Alert>
              )}
              {selectedCallRecording.transcript_text ? (
                <Paper elevation={0} sx={{ p: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                  <Typography fontSize={13} fontWeight={800} sx={{ mb: 1 }}><T>Transcript</T></Typography>
                  <Typography fontSize={14} sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                    <CallTranscript segments={selectedCallRecording.transcript_segments || selectedCallRecording.message_metadata?.transcript_segments} text={selectedCallRecording.transcript_text} />
                  </Typography>
                </Paper>
              ) : (
                <Alert severity="info"><T>
                  No transcript saved yet. Generate it from this recording so AI replies can use the call context.
                </T></Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.25 }}>
          {selectedCallRecording?.call_record_id && (
            <Button
              onClick={() => handleTranscribeCallRecording(selectedCallRecording)}
              disabled={Boolean(transcribingCallId)}
              startIcon={transcribingCallId ? <CircularProgress size={16} /> : <DescriptionIcon />}
              variant="contained"
              sx={{ bgcolor: GREEN, color: '#000', fontWeight: 800, borderRadius: 2, '&:hover': { bgcolor: '#22c55e' } }}
            >
              {selectedCallRecording.transcript_text ? 'Regenerate Transcript' : 'Generate Transcript'}
            </Button>
          )}
          {selectedCallRecording?.transcript_text && (
            <Button onClick={() => openTranscriptDialog(selectedCallRecording)} color="inherit" sx={{ borderRadius: 2 }}><T>
              Open Transcript
            </T></Button>
          )}
          <Button onClick={() => setShowRecordingDialog(false)} color="inherit" sx={{ borderRadius: 2 }}><T>Close</T></Button>
        </DialogActions>
      </Dialog>

      {/* ─── Transcript Dialog ─── */}
      <Dialog open={showTranscript} onClose={() => setShowTranscript(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DescriptionIcon />
          <Typography fontWeight={600}><T>Call Transcript</T></Typography>
          <Box sx={{ flex: 1 }} />
          <IconButton size="small" onClick={() => setShowTranscript(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedTranscript && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}><T>
                Duration: </T>{formatDuration(selectedTranscript.call_duration_seconds)} | {formatTime(selectedTranscript.created_at)}
              </Typography>
              <Paper elevation={0} sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2, whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.8 }}>
                <CallTranscript segments={selectedTranscript.transcript_segments || selectedTranscript.message_metadata?.transcript_segments} text={selectedTranscript.transcript_text || selectedTranscript.message_metadata?.transcript_text} />
              </Paper>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1 }}>
          <Button onClick={() => setShowTranscript(false)} color="inherit" sx={{ borderRadius: 2 }}><T>Close</T></Button>
        </DialogActions>
      </Dialog>

      {/* ─── Browser-based Call Modal ─── */}
      <CallModal
        show={showCallModal}
        onHide={() => {
          setShowCallModal(false);
          fetchConversation();
        }}
        conversationId={conversationId}
        userId={user?.id}
        customerName={conversation?.customer_display_name}
        userDisplayName={user?.first_name ? `Agent (${user.first_name})` : 'Agent'}
      />

      <ActorQuickInfoDialog
        open={actorDialogOpen}
        actorId={customerId}
        fallbackName={conversation?.customer_display_name || 'Client'}
        onClose={() => setActorDialogOpen(false)}
        onActorSaved={handleActorSaved}
      />

      {/* ─── Planned / Scheduled Messages Dialog ─── */}
      <Dialog
        open={showScheduledDialog}
        onClose={() => setShowScheduledDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: `2px solid ${GREEN}`, py: 1.5 }}>
          <ScheduleIcon sx={{ color: '#166534' }} />
          <Typography fontWeight={800} fontSize={16}><T>Scheduled messages</T></Typography>
          <Box sx={{ flex: 1 }} />
          <IconButton size="small" onClick={() => setShowScheduledDialog(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          {!scheduledMessages.length ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}><T>
              No scheduled messages for this conversation.
            </T></Alert>
          ) : (
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              {scheduledMessages.map((item) => (
                <Paper key={item.id} elevation={0} sx={{ p: 2, border: '1px solid #cbd5e1', borderRadius: 2, bgcolor: '#f8fafc' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box>
                      <Chip
                        label={item.channel}
                        size="small"
                        sx={{ height: 20, fontSize: 10, fontWeight: 800, bgcolor: '#dbeafe', color: '#1e40af', mr: 1 }}
                      />
                      <Typography component="span" fontSize={12} fontWeight={700} color="#475569"><T>
                        Scheduled for </T>{formatDDMMYYYY(item.scheduled_at)}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      startIcon={<DeleteIcon sx={{ fontSize: 14 }} />}
                      onClick={() => handleCancelScheduledMessage(item.id)}
                      sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 2, fontSize: 11 }}
                    ><T>
                      Cancel
                    </T></Button>
                  </Box>
                  <Typography fontSize={13} sx={{ whiteSpace: 'pre-wrap', color: '#1e293b', bgcolor: '#fff', p: 1.25, borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
                    {item.content}
                  </Typography>
                  <Typography fontSize={11} color="text.secondary" sx={{ mt: 0.75 }}><T>
                    Absender: </T>{item.sender_display}<T> · Erstellt am: </T>{formatTime(item.created_at)}
                  </Typography>
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button onClick={() => setShowScheduledDialog(false)} color="inherit" sx={{ borderRadius: 2 }}><T>Close</T></Button>
        </DialogActions>
      </Dialog>

      {/* ── Human-in-the-Loop Learning Recommendations Dialog ── */}
      <Dialog open={showLearningRecsModal} onClose={() => setShowLearningRecsModal(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1, borderBottom: '2px solid #16a34a' }}>
          <AutoFixHighIcon sx={{ color: '#16a34a' }} />
          <Typography variant="h6" fontWeight={800} fontSize={16}><T>
            🧠 Continuous Learning Recommendations (Human-in-the-Loop)
          </T></Typography>
          <Box sx={{ flex: 1 }} />
          <IconButton size="small" onClick={() => setShowLearningRecsModal(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          {loadingRecs ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress color="success" /></Box>
          ) : learningRecs.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}><T>
              No pending learning recommendations. The system continuously analyzes agent message edits to propose improvements.
            </T></Alert>
          ) : (
            <Box sx={{ display: 'grid', gap: 2 }}>
              {learningRecs.map(rec => (
                <Paper key={rec.id} variant="outlined" sx={{ p: 2, borderRadius: 2.5, borderColor: '#bbf7d0', bgcolor: '#f0fdf4' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography fontSize={14} fontWeight={800} color="#166534">{rec.title}</Typography>
                    <Chip label={rec.recommendation_type} size="small" sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 800, fontSize: 11 }} />
                  </Box>
                  <Typography fontSize={13} color="#334155" sx={{ mb: 1.5 }}>
                    {rec.suggested_change?.summary}
                  </Typography>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => handleApproveRec(rec.id)}
                    sx={{ bgcolor: '#16a34a', color: '#fff', fontWeight: 800, textTransform: 'none', borderRadius: 2, '&:hover': { bgcolor: '#15803d' } }}
                  ><T>
                    Approve & Apply Improvement
                  </T></Button>
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default ConversationView;
